import { createContext, FormEvent, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "./lib/supabase";

type AuthState = {
  session: Session | null;
  user: User | null;
  loading: boolean;
};

type WorkspaceProduct = {
  product_key: string;
  name: string;
  description: string | null;
  status: string;
  launch_url: string | null;
  access_status: string;
  plan_key: string | null;
};

type WorkspaceData = {
  loading: boolean;
  error: string | null;
  profileName: string | null;
  avatarUrl: string | null;
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  billingEmail: string | null;
  billingCpfCnpj: string | null;
  billingPhone: string | null;
  role: string | null;
  isPlatformAdmin: boolean;
  products: WorkspaceProduct[];
};

const WorkspaceContext = createContext<WorkspaceData | null>(null);

function useWorkspace() {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error("WorkspaceContext não encontrado.");
  }

  return context;
}

function useWorkspaceData(user: User): WorkspaceData {
  const [workspace, setWorkspace] = useState<WorkspaceData>({
    loading: true,
    error: null,
    profileName: null,
    avatarUrl: null,
    organizationId: null,
    organizationName: null,
    organizationSlug: null,
    billingEmail: null,
    billingCpfCnpj: null,
    billingPhone: null,
    role: null,
    isPlatformAdmin: false,
    products: []
  });

  useEffect(() => {
    let active = true;

    if (!supabase) {
      setWorkspace((current) => ({
        ...current,
        loading: false,
        error: "Supabase não configurado."
      }));
      return;
    }

    const client = supabase;

    async function loadWorkspace() {
      const [profileResult, membershipResult] = await Promise.all([
        client
          .from("profiles")
          .select("full_name, avatar_url, is_platform_admin")
          .eq("id", user.id)
          .maybeSingle(),
        client
          .from("organization_members")
          .select("organization_id, role, organizations(id, name, slug, billing_email, billing_cpf_cnpj, billing_phone)")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle()
      ]);

      if (!active) return;

      const firstError = profileResult.error || membershipResult.error;

      if (firstError) {
        setWorkspace((current) => ({
          ...current,
          loading: false,
          error: firstError.message
        }));
        return;
      }

      const membership = membershipResult.data as
        | {
            organization_id: string;
            role: string;
            organizations:
              | {
                  id: string;
                  name: string;
                  slug: string;
                  billing_email: string | null;
                  billing_cpf_cnpj: string | null;
                  billing_phone: string | null;
                }
              | Array<{
                  id: string;
                  name: string;
                  slug: string;
                  billing_email: string | null;
                  billing_cpf_cnpj: string | null;
                  billing_phone: string | null;
                }>
              | null;
          }
        | null;

      const organizationRelation = membership?.organizations;
      const organization = Array.isArray(organizationRelation)
        ? organizationRelation[0]
        : organizationRelation;

      let workspaceProducts: WorkspaceProduct[] = [];
      let accessError: string | null = null;

      if (membership?.organization_id) {
        const accessResult = await client
          .from("organization_products")
          .select(
            "access_status, plan_key, products(product_key, name, description, status, launch_url)"
          )
          .eq("organization_id", membership.organization_id);

        if (accessResult.error) {
          accessError = accessResult.error.message;
        } else {
          workspaceProducts = (accessResult.data ?? [])
            .map((row) => {
              const productRelation = row.products as
                | {
                    product_key: string;
                    name: string;
                    description: string | null;
                    status: string;
                    launch_url: string | null;
                  }
                | Array<{
                    product_key: string;
                    name: string;
                    description: string | null;
                    status: string;
                    launch_url: string | null;
                  }>
                | null;

              const product = Array.isArray(productRelation)
                ? productRelation[0]
                : productRelation;

              if (!product) return null;

              return {
                ...product,
                access_status: row.access_status,
                plan_key: row.plan_key
              } satisfies WorkspaceProduct;
            })
            .filter((product): product is WorkspaceProduct => product !== null)
            .sort((a, b) => a.name.localeCompare(b.name));
        }
      }

      if (!active) return;

      setWorkspace({
        loading: false,
        error: accessError,
        profileName: profileResult.data?.full_name ?? null,
        avatarUrl: profileResult.data?.avatar_url ?? null,
        organizationId: membership?.organization_id ?? null,
        organizationName: organization?.name ?? null,
        organizationSlug: organization?.slug ?? null,
        billingEmail: organization?.billing_email ?? null,
        billingCpfCnpj: organization?.billing_cpf_cnpj ?? null,
        billingPhone: organization?.billing_phone ?? null,
        role: membership?.role ?? null,
        isPlatformAdmin: Boolean(profileResult.data?.is_platform_admin),
        products: workspaceProducts
      });
    }

    const refreshProfile = () => {
      void loadWorkspace();
    };

    void loadWorkspace();
    window.addEventListener("horizonte:profile-changed", refreshProfile);

    return () => {
      active = false;
      window.removeEventListener("horizonte:profile-changed", refreshProfile);
    };
  }, [user.id]);

  return workspace;
}

function translateRole(role: string | null) {
  if (role === "owner") return "Proprietária";
  if (role === "admin") return "Administradora";
  if (role === "member") return "Membro";
  return "Não definido";
}

function translateProductStatus(status: string) {
  if (status === "planned") return "Planejado";
  if (status === "development") return "Em desenvolvimento";
  if (status === "active") return "Ativo";
  if (status === "paused") return "Pausado";
  return status;
}

function translateAccessStatus(status: string) {
  if (status === "trial") return "Período de teste";
  if (status === "active") return "Acesso ativo";
  if (status === "suspended") return "Suspenso";
  if (status === "cancelled") return "Cancelado";
  return status;
}

function translateSubscriptionStatus(status: string) {
  if (status === "trialing") return "Em teste";
  if (status === "active") return "Ativa";
  if (status === "past_due") return "Pagamento pendente";
  if (status === "cancelled") return "Cancelada";
  return status;
}

function translatePaymentStatus(status: string) {
  if (status === "pending") return "Pendente";
  if (status === "paid") return "Pago";
  if (status === "failed") return "Falhou";
  if (status === "refunded") return "Reembolsado";
  if (status === "cancelled") return "Cancelado";
  return status;
}

function translateSupportStatus(status: string) {
  if (status === "open") return "Aberto";
  if (status === "in_progress") return "Em atendimento";
  if (status === "resolved") return "Resolvido";
  if (status === "closed") return "Encerrado";
  return status;
}

function translateSupportCategory(category: string) {
  if (category === "general") return "Geral";
  if (category === "billing") return "Cobrança";
  if (category === "access") return "Acesso";
  if (category === "technical") return "Problema técnico";
  if (category === "account") return "Conta e segurança";
  return category;
}

function translateSupportPriority(priority: string) {
  if (priority === "low") return "Baixa";
  if (priority === "normal") return "Normal";
  if (priority === "high") return "Alta";
  if (priority === "urgent") return "Urgente";
  return priority;
}

function translateBillingCycle(cycle: string) {
  if (cycle === "monthly") return "mensal";
  if (cycle === "yearly") return "anual";
  if (cycle === "custom") return "personalizado";
  return cycle;
}

function formatPlanPrice(priceCents: number, currency = "BRL") {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency
  }).format(priceCents / 100);
}

const products = [
  {
    name: "Central de Projetos",
    category: "Gestão e produtividade",
    description:
      "Projetos organizados em planejamento, estrutura, ciclos, ações, agenda e acompanhamento.",
    status: "Em desenvolvimento"
  },
  {
    name: "Verbum",
    category: "Escrita e criação",
    description:
      "Ambiente estruturado para apoiar escritores na construção, organização e evolução de suas obras.",
    status: "Produto"
  },
  {
    name: "AutoCare",
    category: "Soluções automotivas",
    description:
      "Projeto para organizar atendimento, serviços e processos do segmento automotivo.",
    status: "Planejado"
  }
];

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className={compact ? "brand brand-compact" : "brand"}>
      <img src="/brand-logo.svg" alt="Horizonte dos Sistemas" />
    </Link>
  );
}

function PublicHeader() {
  return (
    <header className="public-header">
      <div className="container header-row">
        <Brand />
        <nav className="public-nav" aria-label="Navegação principal">
          <a href="/#solucoes">Soluções</a>
          <a href="/#produtos">Produtos</a>
          <a href="/#sobre">Sobre</a>
        </nav>
        <div className="header-actions">
          <Link className="button button-ghost" to="/login">
            Entrar
          </Link>
          <Link className="button button-primary" to="/cadastro">
            Criar conta
          </Link>
        </div>
      </div>
    </header>
  );
}

function LandingPage() {
  return (
    <>
      <PublicHeader />
      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">TECNOLOGIA • SISTEMAS • INOVAÇÃO</span>
              <h1>
                Criando sistemas,
                <span> ampliando horizontes.</span>
              </h1>
              <p>
                Uma plataforma criada para transformar ideias em soluções digitais,
                reunir produtos SaaS e oferecer tecnologia com estrutura para crescer.
              </p>
              <div className="hero-actions">
                <Link className="button button-primary button-large" to="/cadastro">
                  Começar agora
                </Link>
                <a className="button button-ghost button-large" href="#produtos">
                  Conhecer produtos
                </a>
              </div>
              <div className="hero-tags">
                <span>Sistemas sob medida</span>
                <span>Plataformas SaaS</span>
                <span>Automação e integrações</span>
              </div>
            </div>
            <div className="hero-art" aria-hidden="true">
              <div className="orbit orbit-one" />
              <div className="orbit orbit-two" />
              <div className="sun" />
              <div className="h-mark">H</div>
              <div className="floating-card card-a">Ideias</div>
              <div className="floating-card card-b">Sistemas</div>
              <div className="floating-card card-c">Horizontes</div>
            </div>
          </div>
        </section>

        <section className="section" id="solucoes">
          <div className="container">
            <div className="section-heading">
              <span className="eyebrow">O QUE CONSTRUÍMOS</span>
              <h2>Uma base para criar, administrar e evoluir soluções digitais.</h2>
              <p>
                A Horizonte dos Sistemas nasce como SaaS: uma estrutura preparada para
                usuários, organizações, produtos, permissões e futuras assinaturas.
              </p>
            </div>
            <div className="feature-grid">
              {[
                ["01", "Sistemas SaaS", "Produtos online com acesso por conta, autenticação e dados separados."],
                ["02", "Sistemas sob medida", "Soluções estruturadas para necessidades específicas de projetos e negócios."],
                ["03", "Automação", "Processos conectados para reduzir trabalho repetitivo e organizar operações."],
                ["04", "Ecossistema", "Uma plataforma principal capaz de reunir diferentes produtos da marca."]
              ].map(([number, title, text]) => (
                <article className="feature-card" key={title}>
                  <span className="feature-number">{number}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-soft" id="produtos">
          <div className="container">
            <div className="section-heading heading-row">
              <div>
                <span className="eyebrow">NOSSO UNIVERSO</span>
                <h2>Produtos que crescem dentro de uma mesma plataforma.</h2>
              </div>
              <p>
                A base SaaS será compartilhada sempre que fizer sentido, evitando
                reconstruir autenticação, contas e administração do zero.
              </p>
            </div>
            <div className="product-grid">
              {products.map((product, index) => (
                <article className={index === 0 ? "product-card featured" : "product-card"} key={product.name}>
                  <span className="status-pill">{translateProductStatus(product.status)}</span>
                  <small>{product.category}</small>
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="sobre">
          <div className="container about-grid">
            <div className="about-visual">
              <div className="about-horizon" />
              <strong>H</strong>
            </div>
            <div>
              <span className="eyebrow">HORIZONTE DOS SISTEMAS</span>
              <h2>Uma empresa criada para levar ideias além.</h2>
              <p className="lead">
                Tecnologia com organização, clareza e evolução contínua. Cada sistema
                nasce com uma finalidade própria, mas pode fazer parte de um ecossistema
                preparado para crescer.
              </p>
              <div className="value-row">
                {["Clareza", "Organização", "Inovação", "Simplicidade", "Evolução"].map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="container cta-box">
            <div>
              <span className="eyebrow eyebrow-light">COMECE PELO PRIMEIRO PASSO</span>
              <h2>Seu acesso à Horizonte começa aqui.</h2>
              <p>Crie uma conta para entrar na base inicial da plataforma SaaS.</p>
            </div>
            <Link className="button button-light button-large" to="/cadastro">
              Criar minha conta
            </Link>
          </div>
        </section>
      </main>
      <footer className="footer">
        <div className="container footer-row">
          <Brand compact />
          <p>Horizonte dos Sistemas · Criando sistemas, ampliando horizontes.</p>
        </div>
      </footer>
    </>
  );
}

function AuthLayout({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="auth-page">
      <div className="auth-brand-panel">
        <Brand />
        <div className="auth-message">
          <span className="eyebrow eyebrow-light">HORIZONTE DOS SISTEMAS</span>
          <h1>Criando sistemas, ampliando horizontes.</h1>
          <p>
            Uma única base para organizar contas, usuários, produtos e o crescimento
            dos nossos sistemas.
          </p>
        </div>
      </div>
      <main className="auth-form-panel">
        <div className="auth-card">
          <Link className="back-link" to="/">
            ← Voltar para o início
          </Link>
          <h2>{title}</h2>
          <p className="auth-subtitle">{subtitle}</p>
          {!supabaseConfigured && (
            <div className="notice">
              <strong>Supabase ainda não configurado.</strong>
              <span>
                A interface está pronta. Para ativar autenticação real, configure
                VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY.
              </span>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailConfirmed = searchParams.get("confirmed") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage("");

    if (!supabase) {
      setMessage("Conecte o Supabase para ativar o login.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    navigate("/app");
  }

  return (
    <AuthLayout title="Entrar" subtitle="Acesse sua conta na Horizonte dos Sistemas.">
      {emailConfirmed && (
        <div className="notice success-notice">
          <strong>E-mail confirmado com sucesso.</strong>
          <span>Agora você já pode entrar na Horizonte dos Sistemas.</span>
        </div>
      )}
      <form className="form-stack" onSubmit={handleSubmit}>
        <label>
          E-mail
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="voce@empresa.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          Senha
          <input
            type="password"
            required
            autoComplete="current-password"
            placeholder="Sua senha"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <div className="form-meta">
          <Link to="/recuperar-senha">Esqueci minha senha</Link>
        </div>
        {message && <p className="form-message">{message}</p>}
        <button className="button button-primary button-full" disabled={busy}>
          {busy ? "Entrando..." : "Entrar"}
        </button>
      </form>
      <p className="auth-switch">
        Ainda não tem conta? <Link to="/cadastro">Criar conta</Link>
      </p>
    </AuthLayout>
  );
}

function RegisterPage() {
  const [form, setForm] = useState({
    fullName: "",
    organizationName: "",
    email: "",
    password: ""
  });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage("");

    if (!supabase) {
      setMessage("Conecte o Supabase para ativar o cadastro.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        emailRedirectTo: window.location.origin + "/auth/confirm",
        data: {
          full_name: form.fullName,
          organization_name: form.organizationName
        }
      }
    });
    setBusy(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage(
      "Cadastro criado. Verifique seu e-mail e clique no link de confirmação. Depois você será direcionada para a tela de login."
    );
  }

  return (
    <AuthLayout title="Criar conta" subtitle="Crie sua organização e o primeiro acesso à plataforma.">
      <form className="form-stack" onSubmit={handleSubmit}>
        <label>
          Seu nome
          <input
            required
            value={form.fullName}
            onChange={(event) => setForm({ ...form, fullName: event.target.value })}
            placeholder="Nome completo"
          />
        </label>
        <label>
          Empresa ou organização
          <input
            required
            value={form.organizationName}
            onChange={(event) => setForm({ ...form, organizationName: event.target.value })}
            placeholder="Nome da organização"
          />
        </label>
        <label>
          E-mail
          <input
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            placeholder="voce@empresa.com"
          />
        </label>
        <label>
          Senha
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            placeholder="Mínimo de 8 caracteres"
          />
        </label>
        {message && <p className="form-message">{message}</p>}
        <button className="button button-primary button-full" disabled={busy}>
          {busy ? "Criando..." : "Criar conta"}
        </button>
      </form>
      <p className="auth-switch">
        Já possui conta? <Link to="/login">Entrar</Link>
      </p>
    </AuthLayout>
  );
}

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!supabase) {
      setMessage("Conecte o Supabase para ativar a recuperação de senha.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/login"
    });
    setBusy(false);
    setMessage(error ? error.message : "Se o e-mail existir, enviaremos as instruções de recuperação.");
  }

  return (
    <AuthLayout title="Recuperar senha" subtitle="Informe seu e-mail para receber as instruções.">
      <form className="form-stack" onSubmit={handleSubmit}>
        <label>
          E-mail
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="voce@empresa.com"
          />
        </label>
        {message && <p className="form-message">{message}</p>}
        <button className="button button-primary button-full" disabled={busy}>
          {busy ? "Enviando..." : "Enviar instruções"}
        </button>
      </form>
    </AuthLayout>
  );
}

function InviteAcceptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get("invitation");
  const [form, setForm] = useState({
    fullName: "",
    password: ""
  });
  const [status, setStatus] = useState("Validando convite...");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase || !invitationId) {
      setStatus("Convite inválido.");
      return;
    }

    const client = supabase;
    let active = true;

    async function checkSession() {
      const { data } = await client.auth.getSession();

      if (!active) return;

      if (data.session?.user) {
        setForm((current) => ({
          ...current,
          fullName:
            data.session?.user.user_metadata?.full_name ??
            current.fullName
        }));
        setStatus("Convite reconhecido. Defina seu acesso para entrar na organização.");
        setReady(true);
        return;
      }

      let attempts = 0;
      const interval = window.setInterval(async () => {
        attempts += 1;
        const { data: current } = await client.auth.getSession();

        if (!active) {
          window.clearInterval(interval);
          return;
        }

        if (current.session?.user) {
          window.clearInterval(interval);
          setForm((previous) => ({
            ...previous,
            fullName:
              current.session?.user.user_metadata?.full_name ??
              previous.fullName
          }));
          setStatus("Convite reconhecido. Defina seu acesso para entrar na organização.");
          setReady(true);
          return;
        }

        if (attempts >= 12) {
          window.clearInterval(interval);
          setStatus(
            "Não foi possível validar o convite. Abra novamente o link recebido por e-mail."
          );
        }
      }, 500);
    }

    void checkSession();

    return () => {
      active = false;
    };
  }, [invitationId]);

  async function acceptInvitation(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !invitationId) return;

    if (!form.fullName.trim() || form.password.length < 8) {
      setStatus("Informe seu nome e uma senha com pelo menos 8 caracteres.");
      return;
    }

    setBusy(true);
    setStatus("Finalizando seu acesso...");

    const { error: userError } = await supabase.auth.updateUser({
      password: form.password,
      data: { full_name: form.fullName.trim() }
    });

    if (userError) {
      setStatus(userError.message);
      setBusy(false);
      return;
    }

    const {
      data: { user }
    } = await supabase.auth.getUser();

    if (user) {
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ full_name: form.fullName.trim() })
        .eq("id", user.id);

      if (profileError) {
        setStatus(profileError.message);
        setBusy(false);
        return;
      }
    }

    const { error: invitationError } = await supabase.rpc(
      "accept_organization_invitation",
      { invitation_id: invitationId }
    );

    if (invitationError) {
      setStatus(invitationError.message);
      setBusy(false);
      return;
    }

    setStatus("Convite aceito. Entrando na Horizonte...");
    navigate("/app", { replace: true });
  }

  return (
    <AuthLayout
      title="Aceitar convite"
      subtitle="Finalize seu acesso à organização na Horizonte dos Sistemas."
    >
      <p className="form-message">{status}</p>

      {ready ? (
        <form className="form-stack" onSubmit={acceptInvitation}>
          <label>
            Seu nome
            <input
              required
              value={form.fullName}
              onChange={(event) =>
                setForm({ ...form, fullName: event.target.value })
              }
              placeholder="Nome completo"
            />
          </label>

          <label>
            Criar senha
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
              placeholder="Mínimo de 8 caracteres"
            />
          </label>

          <button
            className="button button-primary button-full"
            type="submit"
            disabled={busy}
          >
            {busy ? "Finalizando..." : "Aceitar convite"}
          </button>
        </form>
      ) : null}
    </AuthLayout>
  );
}

function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Confirmando seu e-mail...");

  useEffect(() => {
    const client = supabase;

    if (!client) {
      setStatus("Supabase não está configurado.");
      return;
    }

    let active = true;

    const finishConfirmation = async () => {
      // O Supabase processa o token da URL automaticamente porque
      // detectSessionInUrl está habilitado no cliente.
      const { data } = await client.auth.getSession();

      if (!active) return;

      if (data.session) {
        await client.auth.signOut();
        if (active) navigate("/login?confirmed=1", { replace: true });
        return;
      }

      // Aguarda por alguns instantes a sessão criada pelo link de confirmação.
      let attempts = 0;
      const interval = window.setInterval(async () => {
        attempts += 1;
        const { data: current } = await client.auth.getSession();

        if (!active) {
          window.clearInterval(interval);
          return;
        }

        if (current.session) {
          window.clearInterval(interval);
          await client.auth.signOut();
          if (active) navigate("/login?confirmed=1", { replace: true });
          return;
        }

        if (attempts >= 12) {
          window.clearInterval(interval);
          setStatus("Não foi possível concluir a confirmação automaticamente. Tente abrir o link novamente.");
        }
      }, 500);

      return () => window.clearInterval(interval);
    };

    void finishConfirmation();

    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <div className="setup-screen">
      <Brand />
      <div className="setup-card">
        <span className="eyebrow">CONFIRMAÇÃO DE E-MAIL</span>
        <h1>{status}</h1>
        <p>Estamos finalizando seu acesso à Horizonte dos Sistemas.</p>
      </div>
    </div>
  );
}

function AppShell({
  user,
  children
}: {
  user: User;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const workspace = useWorkspaceData(user);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    if (!supabase || !workspace.organizationId) {
      setUnreadNotifications(0);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadUnreadCount() {
      const { count, error } = await client
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", workspace.organizationId)
        .eq("user_id", user.id)
        .is("read_at", null);

      if (!active || error) return;
      setUnreadNotifications(count ?? 0);
    }

    const refresh = () => {
      void loadUnreadCount();
    };

    void loadUnreadCount();
    window.addEventListener("horizonte:notifications-changed", refresh);

    return () => {
      active = false;
      window.removeEventListener("horizonte:notifications-changed", refresh);
    };
  }, [workspace.organizationId, user.id, location.pathname]);

  async function signOut() {
    if (supabase) await supabase.auth.signOut();
    navigate("/login");
  }

  const links = [
    ["/app", "Visão geral"],
    ["/app/sistemas", "Sistemas"],
    ["/app/planos", "Planos"],
    ["/app/pagamentos", "Pagamentos"],
    ["/app/equipe", "Equipe"],
    ["/app/notificacoes", "Notificações"],
    ["/app/atividades", "Histórico"],
    ["/app/suporte", "Suporte"],
    ...(workspace.isPlatformAdmin
      ? [["/admin/dashboard", "Painel Horizonte"]]
      : [])
  ];

  return (
    <WorkspaceContext.Provider value={workspace}>
      <div className="app-layout">
      <aside className="sidebar">
        <Brand compact />
        <nav className="sidebar-nav">
          {links.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app" || to === "/admin"}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <span>{label}</span>
              {label === "Notificações" && unreadNotifications > 0 ? (
                <span className="sidebar-badge">
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <NavLink
            to="/app/configuracoes"
            className={({ isActive }) =>
              isActive
                ? "sidebar-settings-link active"
                : "sidebar-settings-link"
            }
          >
            <svg className="sidebar-footer-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
              <path d="M19.4 13.5a7.8 7.8 0 0 0 .05-3l2-1.55-2-3.46-2.47 1a8.3 8.3 0 0 0-2.58-1.5L14 2h-4l-.4 2.99a8.3 8.3 0 0 0-2.58 1.5l-2.47-1-2 3.46 2 1.55a7.8 7.8 0 0 0 .05 3l-2.05 1.59 2 3.46 2.52-1.02a8.2 8.2 0 0 0 2.53 1.46L10 22h4l.4-3.01a8.2 8.2 0 0 0 2.53-1.46l2.52 1.02 2-3.46-2.05-1.59Z" />
            </svg>
            <span>Configurações</span>
          </NavLink>

          <NavLink
            to="/app/perfil"
            className={({ isActive }) =>
              isActive
                ? "sidebar-profile-card active"
                : "sidebar-profile-card"
            }
          >
            <div className="sidebar-profile-avatar" aria-hidden="true">
              {workspace.avatarUrl ? (
                <img src={workspace.avatarUrl} alt="" />
              ) : (
                (workspace.profileName || user.email || "U")
                  .trim()
                  .charAt(0)
                  .toUpperCase()
              )}
            </div>
            <div className="sidebar-profile-copy">
              <strong>{workspace.profileName || "Usuário Horizonte"}</strong>
              <span>Ver perfil</span>
            </div>
          </NavLink>

          <button className="sidebar-logout" type="button" onClick={signOut}>
            <svg className="sidebar-footer-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M10 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h5" />
              <path d="M14 8l4 4-4 4" />
              <path d="M18 12H8" />
            </svg>
            <span>Sair</span>
          </button>
        </div>
      </aside>
      <div className="app-main">
        <header className="app-topbar">
          <div>
            <small>{workspace.organizationName ?? "Horizonte SaaS"}</small>
            <strong>
              {location.pathname.startsWith("/admin")
                ? "Administração da plataforma"
                : location.pathname === "/app"
                  ? "Visão geral"
                  : "Área da organização"}
            </strong>
          </div>
          <span className="phase-badge">Temporada 1 · v1.0</span>
        </header>
        <main className="app-content">{children}</main>
      </div>
    </div>
    </WorkspaceContext.Provider>
  );
}

function DashboardPage() {
  const workspace = useWorkspace();
  const accessibleProducts = workspace.products.filter((product) =>
    ["trial", "active"].includes(product.access_status)
  );

  if (workspace.loading) {
    return <div className="panel">Carregando dados da organização...</div>;
  }

  return (
    <div className="dashboard-stack">
      <section className="welcome-panel">
        <div>
          <span className="eyebrow eyebrow-light">ÁREA DA ORGANIZAÇÃO</span>
          <h1>
            {workspace.profileName
              ? `Olá, ${workspace.profileName}.`
              : "Bem-vinda à Horizonte dos Sistemas."}
          </h1>
          <p>
            {workspace.organizationName
              ? `Você está acessando a organização ${workspace.organizationName}.`
              : "Sua conta está ativa. Estamos finalizando os dados da sua organização."}
          </p>
        </div>
        <span className="welcome-mark">H</span>
      </section>

      {workspace.error && (
        <section className="panel">
          <small>ATENÇÃO</small>
          <h2>Não foi possível carregar todos os dados.</h2>
          <p>{workspace.error}</p>
        </section>
      )}

      <div className="dashboard-grid">
        <section className="panel panel-wide">
          <div className="panel-heading">
            <div>
              <small>SISTEMAS LIBERADOS PARA SUA ORGANIZAÇÃO</small>
              <h2>Ecossistema Horizonte</h2>
            </div>
            <Link to="/app/sistemas">Ver sistemas →</Link>
          </div>
          <div className="compact-products">
            {accessibleProducts.length > 0 ? (
              accessibleProducts.map((product) => (
                <div key={product.product_key}>
                  <strong>{product.name}</strong>
                  <span>{translateAccessStatus(product.access_status)}</span>
                </div>
              ))
            ) : (
              <div>
                <strong>Nenhum sistema liberado</strong>
                <span>Aguardando liberação de acesso</span>
              </div>
            )}
          </div>
        </section>

        <section className="panel">
          <small>ORGANIZAÇÃO</small>
          <h2>{workspace.organizationName ?? "Não identificada"}</h2>
          <p>
            Perfil de acesso: <strong>{translateRole(workspace.role)}</strong>
          </p>
        </section>

        <section className="panel">
          <small>ACESSOS</small>
          <h2>{accessibleProducts.length} sistema{accessibleProducts.length === 1 ? "" : "s"} liberado{accessibleProducts.length === 1 ? "" : "s"}</h2>
          <p>
            A organização pode acompanhar acessos ativos, em teste, suspensos ou cancelados em um só lugar.
          </p>
        </section>
      </div>
    </div>
  );
}

function SystemsPage() {
  const workspace = useWorkspace();
  const [openingProductKey, setOpeningProductKey] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  async function openSystem(product: WorkspaceProduct) {
    if (!supabase) {
      setMessage("Supabase não configurado.");
      return;
    }

    setOpeningProductKey(product.product_key);
    setMessage("");

    try {
      const {
        data: { session: currentSession }
      } = await supabase.auth.getSession();

      if (!currentSession?.access_token) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const {
        data: { session: refreshedSession },
        error: refreshError
      } = await supabase.auth.refreshSession();

      const session = refreshedSession ?? currentSession;

      if (refreshError || !session?.access_token) {
        setMessage(
          "Sua sessão da Horizonte precisa ser renovada. Saia e entre novamente para abrir os sistemas."
        );
        return;
      }

      const launchRequest = (accessToken: string) =>
        fetch("/api/access/launch", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken
          },
          body: JSON.stringify({
            productKey: product.product_key
          })
        });

      let response = await launchRequest(session.access_token);

      if (response.status === 401) {
        const {
          data: { session: retrySession },
          error: retryError
        } = await supabase.auth.refreshSession();

        if (!retryError && retrySession?.access_token) {
          response = await launchRequest(retrySession.access_token);
        }
      }

      const responseText = await response.text();
      let result: { url?: string; error?: string } | null = null;

      if (responseText) {
        try {
          result = JSON.parse(responseText) as {
            url?: string;
            error?: string;
          };
        } catch {
          result = null;
        }
      }

      if (!response.ok || !result?.url) {
        setMessage(
          result?.error ||
            (responseText && responseText.length < 320
              ? responseText
              : "") ||
            "Não foi possível preparar o acesso integrado ao sistema."
        );
        return;
      }

      window.location.assign(result.url);
    } catch (error) {
      setMessage(
        "Não foi possível abrir o sistema: " +
          (error instanceof Error ? error.message : "falha inesperada.")
      );
    } finally {
      setOpeningProductKey(null);
    }
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">SISTEMAS · CICLO 15</span>
        <h1>Produtos da Horizonte</h1>
        <p>
          Acesse os sistemas liberados para sua organização e acompanhe o status de cada acesso.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <div className="product-grid app-products">
        {workspace.products.length > 0 ? (
          workspace.products.map((product) => {
            const accessAllowed = ["trial", "active"].includes(
              product.access_status
            );
            const canOpen =
              accessAllowed &&
              product.status !== "paused" &&
              Boolean(product.launch_url);

            return (
              <article className="product-card system-access-card" key={product.product_key}>
                <div className="system-access-badges">
                  <span className="status-pill">
                    {translateAccessStatus(product.access_status)}
                  </span>
                  <span className="status-pill status-pill-soft">
                    {translateProductStatus(product.status)}
                  </span>
                </div>

                <small>Produto Horizonte</small>
                <h3>{product.name}</h3>
                <p>{product.description}</p>

                <div className="system-access-action">
                  {canOpen ? (
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={openingProductKey === product.product_key}
                      onClick={() => void openSystem(product)}
                    >
                      {openingProductKey === product.product_key
                        ? "Abrindo..."
                        : "Abrir sistema"}
                    </button>
                  ) : (
                    <button className="button button-secondary" type="button" disabled>
                      {product.access_status === "suspended"
                        ? "Acesso suspenso"
                        : product.access_status === "cancelled"
                          ? "Acesso encerrado"
                          : product.status === "paused"
                            ? "Sistema pausado"
                            : "Acesso em configuração"}
                    </button>
                  )}
                </div>
              </article>
            );
          })
        ) : (
          <section className="panel empty-state">
            <strong>Nenhum sistema vinculado.</strong>
            <p>Quando um produto for liberado para sua organização, ele aparecerá aqui.</p>
          </section>
        )}
      </div>
    </div>
  );
}

function PlansPage() {
  const workspace = useWorkspace();
  const [plans, setPlans] = useState<SaaSPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<SaaSSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] =
    useState<string | null>(null);

  const canManageSubscription = ["owner", "admin"].includes(
    workspace.role ?? ""
  );

  async function loadPlans() {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const [plansResult, subscriptionsResult] = await Promise.all([
      supabase
        .from("plans")
        .select(
          "id, product_id, plan_key, name, description, price_cents, currency, billing_cycle, trial_days, is_active, products(product_key, name)"
        )
        .eq("is_active", true)
        .order("price_cents"),
      supabase
        .from("subscriptions")
        .select(
          "id, organization_id, product_id, plan_id, status, starts_at, trial_ends_at, current_period_end, organizations(name), products(name), plans(name, plan_key)"
        )
        .eq("organization_id", workspace.organizationId)
    ]);

    const firstError = plansResult.error || subscriptionsResult.error;

    if (firstError) {
      setMessage(firstError.message);
      setLoading(false);
      return;
    }

    setPlans((plansResult.data ?? []) as SaaSPlan[]);
    setSubscriptions(
      (subscriptionsResult.data ?? []) as SaaSSubscription[]
    );
    setLoading(false);
  }

  useEffect(() => {
    void loadPlans();
  }, [workspace.organizationId]);

  async function manageSubscription(
    action: "subscribe" | "cancel",
    planId?: string,
    subscriptionId?: string
  ) {
    if (!supabase || !workspace.organizationId || !canManageSubscription) {
      setMessage(
        "Somente proprietária ou administradora pode gerenciar assinaturas."
      );
      return;
    }

    if (action === "cancel" && subscriptionId) {
      const confirmed = window.confirm(
        "Cancelar esta assinatura agora? O acesso ao sistema será encerrado."
      );

      if (!confirmed) return;
      setCancellingSubscriptionId(subscriptionId);
    } else if (planId) {
      setBusyPlanId(planId);
    }

    setMessage(
      action === "cancel"
        ? "Cancelando assinatura..."
        : "Processando plano..."
    );

    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const response = await fetch("/api/subscriptions/manage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + session.access_token
        },
        body: JSON.stringify({
          organizationId: workspace.organizationId,
          action,
          planId,
          subscriptionId
        })
      });

      const responseText = await response.text();
      let result: { error?: string; message?: string } | null = null;

      if (responseText) {
        try {
          result = JSON.parse(responseText) as {
            error?: string;
            message?: string;
          };
        } catch {
          result = null;
        }
      }

      if (!response.ok) {
        setMessage(
          result?.error ||
            (responseText && responseText.length < 240
              ? responseText
              : "") ||
            "Não foi possível atualizar a assinatura."
        );
        return;
      }

      setMessage(
        result?.message ??
          (action === "cancel"
            ? "Assinatura cancelada."
            : "Plano atualizado com sucesso.")
      );

      await loadPlans();
    } catch (error) {
      setMessage(
        "Erro ao atualizar assinatura: " +
          (error instanceof Error ? error.message : "falha inesperada.")
      );
    } finally {
      setBusyPlanId(null);
      setCancellingSubscriptionId(null);
    }
  }

  if (loading) {
    return <section className="panel">Carregando planos...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">PLANOS E ASSINATURAS · CICLO 16</span>
        <h1>Planos da sua organização</h1>
        <p>
          Escolha um plano, inicie um período de teste, altere a assinatura ou
          encerre um acesso diretamente pela plataforma.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <div className="subscription-grid">
        {subscriptions.length > 0 ? (
          subscriptions.map((subscription) => {
            const productRelation = subscription.products;
            const product = Array.isArray(productRelation)
              ? productRelation[0]
              : productRelation;
            const planRelation = subscription.plans;
            const plan = Array.isArray(planRelation)
              ? planRelation[0]
              : planRelation;

            return (
              <section
                className="panel subscription-card commercial-subscription-card"
                key={subscription.id}
              >
                <div className="commercial-card-heading">
                  <div>
                    <small>ASSINATURA ATUAL</small>
                    <h2>{product?.name ?? "Sistema Horizonte"}</h2>
                  </div>
                  <span className="status-pill">
                    {translateSubscriptionStatus(subscription.status)}
                  </span>
                </div>

                <p>
                  Plano: <strong>{plan?.name ?? "Sem plano identificado"}</strong>
                </p>

                {subscription.trial_ends_at &&
                subscription.status === "trialing" ? (
                  <p>
                    Teste até:{" "}
                    <strong>
                      {new Date(subscription.trial_ends_at).toLocaleDateString(
                        "pt-BR"
                      )}
                    </strong>
                  </p>
                ) : null}

                {canManageSubscription &&
                subscription.status !== "cancelled" ? (
                  <button
                    className="text-button text-button-danger"
                    type="button"
                    disabled={
                      cancellingSubscriptionId === subscription.id
                    }
                    onClick={() =>
                      void manageSubscription(
                        "cancel",
                        undefined,
                        subscription.id
                      )
                    }
                  >
                    {cancellingSubscriptionId === subscription.id
                      ? "Cancelando..."
                      : "Cancelar assinatura"}
                  </button>
                ) : null}
              </section>
            );
          })
        ) : (
          <section className="panel empty-state">
            <strong>Nenhuma assinatura encontrada.</strong>
            <p>Escolha um dos planos disponíveis abaixo para começar.</p>
          </section>
        )}
      </div>

      <div className="page-title plans-title">
        <span className="eyebrow">PLANOS DISPONÍVEIS</span>
        <h2>Escolha como continuar</h2>
      </div>

      <div className="plan-grid">
        {plans.map((plan) => {
          const productRelation = plan.products;
          const product = Array.isArray(productRelation)
            ? productRelation[0]
            : productRelation;

          const currentSubscription = subscriptions.find(
            (subscription) =>
              subscription.product_id === plan.product_id &&
              subscription.status !== "cancelled"
          );

          const isCurrentPlan =
            currentSubscription?.plan_id === plan.id;

          const actionLabel = isCurrentPlan
            ? "Plano atual"
            : currentSubscription
              ? "Alterar para este plano"
              : plan.trial_days > 0
                ? "Iniciar período de teste"
                : "Escolher plano";

          return (
            <article className="plan-card commercial-plan-card" key={plan.id}>
              <span className="status-pill">
                {product?.name ?? "Produto Horizonte"}
              </span>
              <h3>{plan.name}</h3>
              <strong className="plan-price">
                {formatPlanPrice(plan.price_cents, plan.currency)}
              </strong>
              <p>{plan.description ?? "Plano disponível para este sistema."}</p>
              <small>
                Cobrança {translateBillingCycle(plan.billing_cycle)}
                {plan.trial_days > 0
                  ? ` · ${plan.trial_days} dias de teste`
                  : ""}
              </small>

              <div className="commercial-plan-action">
                <button
                  className={
                    isCurrentPlan
                      ? "button button-secondary"
                      : "button button-primary"
                  }
                  type="button"
                  disabled={
                    !canManageSubscription ||
                    isCurrentPlan ||
                    busyPlanId === plan.id
                  }
                  onClick={() =>
                    void manageSubscription("subscribe", plan.id)
                  }
                >
                  {!canManageSubscription
                    ? "Somente gestão"
                    : busyPlanId === plan.id
                      ? "Processando..."
                      : actionLabel}
                </button>

                {plan.price_cents > 0 && plan.trial_days === 0 ? (
                  <span>
                    A escolha cria uma cobrança pendente para concluir a ativação.
                  </span>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function PaymentsPage() {
  const workspace = useWorkspace();
  const [payments, setPayments] = useState<SaaSPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadPayments() {
      const { data, error } = await client
        .from("payments")
        .select(
          "id, organization_id, subscription_id, amount_cents, currency, status, due_date, paid_at, provider, provider_payment_id, provider_status, invoice_url, payment_method, organizations(name)"
        )
        .eq("organization_id", workspace.organizationId)
        .order("created_at", { ascending: false });

      if (!active) return;

      if (error) {
        setMessage(error.message);
        setLoading(false);
        return;
      }

      setPayments((data ?? []) as SaaSPayment[]);
      setLoading(false);
    }

    void loadPayments();

    return () => {
      active = false;
    };
  }, [workspace.organizationId]);

  if (loading) {
    return <section className="panel">Carregando pagamentos...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">PAGAMENTOS</span>
        <h1>Histórico de cobranças</h1>
        <p>Acompanhe cobranças, vencimentos e pagamentos da sua organização.</p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel">
        <div className="payment-table">
          {payments.length > 0 ? (
            payments.map((payment) => (
              <div className="payment-row" key={payment.id}>
                <strong>{formatPlanPrice(payment.amount_cents, payment.currency)}</strong>
                <span>{translatePaymentStatus(payment.status)}</span>
                <span>
                  {payment.due_date
                    ? new Date(payment.due_date + "T12:00:00").toLocaleDateString("pt-BR")
                    : "Sem vencimento"}
                </span>
                <span>
                  {payment.payment_method ?? "Método não definido"}
                  {payment.invoice_url ? (
                    <>
                      {" · "}
                      <a href={payment.invoice_url} target="_blank" rel="noreferrer">
                        Abrir cobrança
                      </a>
                    </>
                  ) : null}
                </span>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <strong>Nenhuma cobrança registrada.</strong>
              <p>Quando houver uma cobrança, ela aparecerá aqui.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

type AppNotification = {
  id: string;
  organization_id: string;
  user_id: string;
  notification_type: string;
  title: string;
  message: string | null;
  action_url: string | null;
  read_at: string | null;
  created_at: string;
};

type ActivityEvent = {
  id: string;
  organization_id: string;
  actor_user_id: string | null;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  title: string;
  description: string | null;
  action_url: string | null;
  created_at: string;
};

function NotificationsPage() {
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function loadNotifications() {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("notifications")
      .select(
        "id, organization_id, user_id, notification_type, title, message, action_url, read_at, created_at"
      )
      .eq("organization_id", workspace.organizationId)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }

    setNotifications((data ?? []) as AppNotification[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadNotifications();
  }, [workspace.organizationId]);

  function announceNotificationChange() {
    window.dispatchEvent(new Event("horizonte:notifications-changed"));
  }

  async function markAsRead(notification: AppNotification, open = false) {
    if (!supabase) return;

    if (!notification.read_at) {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notification.id);

      if (error) {
        setMessage(error.message);
        return;
      }
    }

    announceNotificationChange();

    if (open && notification.action_url) {
      navigate(notification.action_url);
      return;
    }

    await loadNotifications();
  }

  async function markAllAsRead() {
    if (!supabase || !workspace.organizationId) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("organization_id", workspace.organizationId)
      .is("read_at", null);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Todas as notificações foram marcadas como lidas.");
    announceNotificationChange();
    await loadNotifications();
  }

  async function deleteNotification(notificationId: string) {
    if (!supabase) return;

    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (error) {
      setMessage(error.message);
      return;
    }

    announceNotificationChange();
    await loadNotifications();
  }

  if (loading) {
    return <section className="panel">Carregando notificações...</section>;
  }

  const unreadCount = notifications.filter((item) => !item.read_at).length;

  return (
    <div>
      <div className="page-title page-title-actions">
        <div>
          <span className="eyebrow">NOTIFICAÇÕES · CICLO 10</span>
          <h1>Central de notificações</h1>
          <p>Acompanhe acontecimentos importantes da sua organização.</p>
        </div>

        {unreadCount > 0 ? (
          <button
            className="button button-secondary"
            type="button"
            onClick={() => void markAllAsRead()}
          >
            Marcar todas como lidas
          </button>
        ) : null}
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel">
        <div className="notification-list">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <article
                className={
                  notification.read_at
                    ? "notification-item"
                    : "notification-item notification-unread"
                }
                key={notification.id}
              >
                <div className="notification-dot" aria-hidden="true" />
                <div className="notification-content">
                  <div className="notification-heading">
                    <strong>{notification.title}</strong>
                    <time>
                      {new Date(notification.created_at).toLocaleString("pt-BR")}
                    </time>
                  </div>
                  {notification.message ? <p>{notification.message}</p> : null}

                  <div className="notification-actions">
                    {!notification.read_at ? (
                      <button
                        className="text-button"
                        type="button"
                        onClick={() => void markAsRead(notification)}
                      >
                        Marcar como lida
                      </button>
                    ) : null}

                    {notification.action_url ? (
                      <button
                        className="text-button"
                        type="button"
                        onClick={() => void markAsRead(notification, true)}
                      >
                        Abrir
                      </button>
                    ) : null}

                    <button
                      className="text-button text-button-danger"
                      type="button"
                      onClick={() => void deleteNotification(notification.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="empty-state">
              <strong>Nenhuma notificação por enquanto.</strong>
              <p>Quando algo importante acontecer, aparecerá aqui.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ActivityHistoryPage() {
  const workspace = useWorkspace();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadActivity() {
      const { data, error } = await client
        .from("activity_events")
        .select(
          "id, organization_id, actor_user_id, event_type, entity_type, entity_id, title, description, action_url, created_at"
        )
        .eq("organization_id", workspace.organizationId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (!active) return;

      if (error) {
        setMessage(error.message);
        setLoading(false);
        return;
      }

      setEvents((data ?? []) as ActivityEvent[]);
      setLoading(false);
    }

    void loadActivity();

    return () => {
      active = false;
    };
  }, [workspace.organizationId]);

  if (loading) {
    return <section className="panel">Carregando histórico...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">HISTÓRICO · CICLO 10</span>
        <h1>Atividades da organização</h1>
        <p>
          Uma trilha cronológica das mudanças importantes realizadas na plataforma.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel">
        <div className="activity-timeline">
          {events.length > 0 ? (
            events.map((event) => (
              <article className="activity-item" key={event.id}>
                <div className="activity-marker" aria-hidden="true" />
                <div>
                  <div className="activity-heading">
                    <strong>{event.title}</strong>
                    <time>{new Date(event.created_at).toLocaleString("pt-BR")}</time>
                  </div>
                  {event.description ? <p>{event.description}</p> : null}
                  {event.action_url ? (
                    <Link className="activity-link" to={event.action_url}>
                      Ver área relacionada →
                    </Link>
                  ) : null}
                </div>
              </article>
            ))
          ) : (
            <div className="empty-state">
              <strong>O histórico começa agora.</strong>
              <p>
                As próximas alterações de equipe, pagamentos, assinaturas e acessos
                serão registradas automaticamente.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

type TeamMember = {
  organization_id: string;
  user_id: string;
  role: string;
  created_at: string;
  full_name: string | null;
};

type TeamInvitation = {
  id: string;
  organization_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  sent_at: string | null;
  delivery_error: string | null;
};

function TeamPage() {
  const workspace = useWorkspace();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "member"
  });

  const canManageTeam = ["owner", "admin"].includes(workspace.role ?? "");

  async function loadTeam() {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setMessage("");

    const { data: memberRows, error: memberError } = await supabase
      .from("organization_members")
      .select("organization_id, user_id, role, created_at")
      .eq("organization_id", workspace.organizationId)
      .order("created_at", { ascending: true });

    if (memberError) {
      setMessage(memberError.message);
      setLoading(false);
      return;
    }

    const userIds = (memberRows ?? []).map((item) => item.user_id);
    let profileById = new Map<string, string | null>();

    if (userIds.length > 0) {
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      if (profileError) {
        setMessage(profileError.message);
        setLoading(false);
        return;
      }

      profileById = new Map(
        (profiles ?? []).map((profile) => [profile.id, profile.full_name])
      );
    }

    setMembers(
      (memberRows ?? []).map((member) => ({
        ...member,
        full_name: profileById.get(member.user_id) ?? null
      }))
    );

    if (canManageTeam) {
      const { data: invitationRows, error: invitationError } = await supabase
        .from("organization_invitations")
        .select(
          "id, organization_id, email, role, status, expires_at, sent_at, delivery_error"
        )
        .eq("organization_id", workspace.organizationId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (invitationError) {
        setMessage(invitationError.message);
      } else {
        setInvitations((invitationRows ?? []) as TeamInvitation[]);
      }
    } else {
      setInvitations([]);
    }

    setLoading(false);
  }

  useEffect(() => {
    void loadTeam();
  }, [workspace.organizationId]);

  async function sendInvitation(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.organizationId || !canManageTeam) return;

    const email = inviteForm.email.trim().toLowerCase();

    if (!email || !email.includes("@")) {
      setMessage("Informe um e-mail válido para o convite.");
      return;
    }

    setInviteBusy(true);
    setMessage("Enviando convite...");

    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const response = await fetch("/api/team/invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + session.access_token
        },
        body: JSON.stringify({
          organizationId: workspace.organizationId,
          email,
          role: inviteForm.role
        })
      });

      const text = await response.text();
      let result: { error?: string; message?: string } | null = null;

      if (text) {
        try {
          result = JSON.parse(text) as { error?: string; message?: string };
        } catch {
          result = null;
        }
      }

      if (!response.ok) {
        setMessage(
          result?.error ||
            (text && text.length < 240 ? text : "") ||
            "Não foi possível enviar o convite."
        );
        return;
      }

      setMessage(result?.message ?? "Convite enviado por e-mail com sucesso.");
      setInviteForm({ email: "", role: "member" });
      await loadTeam();
    } catch (error) {
      setMessage(
        "Erro ao enviar convite: " +
          (error instanceof Error ? error.message : "falha inesperada.")
      );
    } finally {
      setInviteBusy(false);
    }
  }

  async function changeRole(member: TeamMember, nextRole: string) {
    if (!supabase || !canManageTeam) return;

    if (member.role === "owner") {
      setMessage("A proprietária da organização não pode ter a função alterada por esta tela.");
      return;
    }

    const { error } = await supabase
      .from("organization_members")
      .update({ role: nextRole })
      .eq("organization_id", member.organization_id)
      .eq("user_id", member.user_id);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Permissão atualizada com sucesso.");
    await loadTeam();
  }

  async function removeMember(member: TeamMember) {
    if (!supabase || !canManageTeam) return;

    if (member.role === "owner") {
      setMessage("A proprietária não pode ser removida da organização.");
      return;
    }

    const confirmed = window.confirm(
      `Remover ${member.full_name ?? "este membro"} da organização?`
    );

    if (!confirmed) return;

    const { error } = await supabase
      .from("organization_members")
      .delete()
      .eq("organization_id", member.organization_id)
      .eq("user_id", member.user_id);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Membro removido da organização.");
    await loadTeam();
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">EQUIPE · CICLO 09</span>
        <h1>Usuários e permissões</h1>
        <p>
          Veja quem faz parte da organização e controle o nível de acesso da equipe.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel">
        <div className="panel-heading">
          <div>
            <small>MEMBROS DA ORGANIZAÇÃO</small>
            <h2>{workspace.organizationName ?? "Organização"}</h2>
          </div>
          <span className="status-pill">
            {members.length} {members.length === 1 ? "membro" : "membros"}
          </span>
        </div>

        {loading ? (
          <p>Carregando equipe...</p>
        ) : members.length > 0 ? (
          <div className="team-list">
            {members.map((member) => (
              <div className="team-row" key={member.user_id}>
                <div className="team-person">
                  <div className="team-avatar" aria-hidden="true">
                    {(member.full_name ?? "U").trim().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <strong>{member.full_name ?? "Usuário"}</strong>
                    <span>
                      Entrou em{" "}
                      {new Date(member.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </div>

                <div className="team-actions">
                  <select
                    value={member.role}
                    disabled={!canManageTeam || member.role === "owner"}
                    onChange={(event) =>
                      void changeRole(member, event.target.value)
                    }
                    aria-label={`Função de ${member.full_name ?? "membro"}`}
                  >
                    <option value="owner">Proprietária</option>
                    <option value="admin">Administradora</option>
                    <option value="member">Membro</option>
                  </select>

                  {canManageTeam && member.role !== "owner" ? (
                    <button
                      className="button button-danger"
                      type="button"
                      onClick={() => void removeMember(member)}
                    >
                      Remover
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <strong>Nenhum membro encontrado.</strong>
            <p>Quando houver membros, eles aparecerão aqui.</p>
          </div>
        )}
      </section>

      {canManageTeam ? (
        <section className="panel team-next-card">
          <small>CONVITES DE EQUIPE</small>
          <h2>Adicionar novo membro</h2>
          <p>
            Envie um convite por e-mail. O link é válido por 7 dias e só pode ser
            aceito pelo mesmo endereço convidado.
          </p>

          <form className="team-invite-form" onSubmit={sendInvitation}>
            <label>
              E-mail
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(event) =>
                  setInviteForm({ ...inviteForm, email: event.target.value })
                }
                placeholder="pessoa@empresa.com"
              />
            </label>

            <label>
              Permissão inicial
              <select
                value={inviteForm.role}
                onChange={(event) =>
                  setInviteForm({ ...inviteForm, role: event.target.value })
                }
              >
                <option value="member">Membro</option>
                <option value="admin">Administradora</option>
              </select>
            </label>

            <button
              className="button button-primary"
              type="submit"
              disabled={inviteBusy}
            >
              {inviteBusy ? "Enviando..." : "Enviar convite"}
            </button>
          </form>

          {invitations.length > 0 ? (
            <div className="team-invitations">
              <small>CONVITES PENDENTES</small>
              {invitations.map((invitation) => (
                <div className="team-invitation-row" key={invitation.id}>
                  <div>
                    <strong>{invitation.email}</strong>
                    <span>
                      {invitation.role === "admin" ? "Administradora" : "Membro"}
                      {" · "}
                      expira em{" "}
                      {new Date(invitation.expires_at).toLocaleDateString("pt-BR")}
                    </span>
                    {invitation.delivery_error ? (
                      <span className="team-invite-error">
                        {invitation.delivery_error}
                      </span>
                    ) : null}
                  </div>
                  <span className="status-pill">
                    {invitation.sent_at ? "Enviado" : "Pendente"}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

type SupportRequest = {
  id: string;
  organization_id: string;
  requester_user_id: string | null;
  category: string;
  priority: string;
  subject: string;
  message: string;
  status: string;
  admin_response: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  organizations?:
    | { name: string }
    | Array<{ name: string }>
    | null;
  profiles?:
    | { full_name: string | null }
    | Array<{ full_name: string | null }>
    | null;
};

function SupportPage() {
  const workspace = useWorkspace();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    category: "general",
    priority: "normal",
    subject: "",
    message: ""
  });

  async function loadRequests() {
    if (!supabase || !workspace.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("support_requests")
      .select(
        "id, organization_id, requester_user_id, category, priority, subject, message, status, admin_response, resolved_at, created_at, updated_at"
      )
      .eq("organization_id", workspace.organizationId)
      .order("created_at", { ascending: false });

    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }

    setRequests((data ?? []) as SupportRequest[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadRequests();
  }, [workspace.organizationId]);

  async function createSupportRequest(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.organizationId) return;

    if (!form.subject.trim() || !form.message.trim()) {
      setMessage("Informe o assunto e descreva o que aconteceu.");
      return;
    }

    setSending(true);
    setMessage("");

    const {
      data: { user },
      error: userError
    } = await supabase.auth.getUser();

    if (userError || !user) {
      setMessage("Sua sessão expirou. Entre novamente.");
      setSending(false);
      return;
    }

    const { error } = await supabase.from("support_requests").insert({
      organization_id: workspace.organizationId,
      requester_user_id: user.id,
      category: form.category,
      priority: form.priority,
      subject: form.subject.trim(),
      message: form.message.trim(),
      status: "open"
    });

    setSending(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    setForm({
      category: "general",
      priority: "normal",
      subject: "",
      message: ""
    });
    setMessage("Solicitação enviada para a equipe da Horizonte.");
    await loadRequests();
  }

  if (loading) {
    return <section className="panel">Carregando suporte...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">SUPORTE · CICLO 18</span>
        <h1>Central de suporte</h1>
        <p>
          Registre dúvidas, problemas de acesso, cobrança ou questões técnicas e acompanhe o atendimento.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel admin-plan-form">
        <small>NOVA SOLICITAÇÃO</small>
        <h2>Como podemos ajudar?</h2>

        <form className="support-form-grid" onSubmit={createSupportRequest}>
          <label>
            Categoria
            <select
              value={form.category}
              onChange={(event) =>
                setForm({ ...form, category: event.target.value })
              }
            >
              <option value="general">Geral</option>
              <option value="billing">Cobrança</option>
              <option value="access">Acesso</option>
              <option value="technical">Problema técnico</option>
              <option value="account">Conta e segurança</option>
            </select>
          </label>

          <label>
            Prioridade
            <select
              value={form.priority}
              onChange={(event) =>
                setForm({ ...form, priority: event.target.value })
              }
            >
              <option value="low">Baixa</option>
              <option value="normal">Normal</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </label>

          <label className="support-form-wide">
            Assunto
            <input
              required
              value={form.subject}
              onChange={(event) =>
                setForm({ ...form, subject: event.target.value })
              }
              placeholder="Resuma o que você precisa"
            />
          </label>

          <label className="support-form-wide">
            Descrição
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(event) =>
                setForm({ ...form, message: event.target.value })
              }
              placeholder="Conte o que aconteceu e, se possível, em qual tela."
            />
          </label>

          <button
            className="button button-primary"
            type="submit"
            disabled={sending}
          >
            {sending ? "Enviando..." : "Enviar solicitação"}
          </button>
        </form>
      </section>

      <section className="panel">
        <small>MEUS ATENDIMENTOS</small>
        <h2>Histórico de suporte</h2>

        <div className="support-list">
          {requests.length > 0 ? (
            requests.map((request) => (
              <article className="support-card" key={request.id}>
                <div className="support-card-heading">
                  <div>
                    <strong>{request.subject}</strong>
                    <span>
                      {translateSupportCategory(request.category)} ·{" "}
                      {translateSupportPriority(request.priority)} ·{" "}
                      {new Date(request.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <span className="status-pill">
                    {translateSupportStatus(request.status)}
                  </span>
                </div>

                <p>{request.message}</p>

                {request.admin_response ? (
                  <div className="support-response">
                    <small>RESPOSTA DA HORIZONTE</small>
                    <p>{request.admin_response}</p>
                  </div>
                ) : (
                  <small className="support-awaiting">
                    Aguardando retorno da equipe.
                  </small>
                )}
              </article>
            ))
          ) : (
            <div className="empty-state">
              <strong>Nenhuma solicitação aberta.</strong>
              <p>Quando precisar, envie uma mensagem pelo formulário acima.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AdminSupportPage() {
  const workspace = useWorkspace();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    status: "open",
    priority: "normal",
    response: ""
  });

  async function loadRequests() {
    if (!supabase || !workspace.isPlatformAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("support_requests")
      .select(
        "id, organization_id, requester_user_id, category, priority, subject, message, status, admin_response, resolved_at, created_at, updated_at, organizations(name), profiles(full_name)"
      )
      .order("created_at", { ascending: false });

    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }

    setRequests((data ?? []) as SupportRequest[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadRequests();
  }, [workspace.isPlatformAdmin]);

  function startSupportEdit(request: SupportRequest) {
    setEditingId(request.id);
    setEditForm({
      status: request.status,
      priority: request.priority,
      response: request.admin_response ?? ""
    });
    setMessage("");
  }

  async function saveSupportRequest(requestId: string) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const { error } = await supabase
      .from("support_requests")
      .update({
        status: editForm.status,
        priority: editForm.priority,
        admin_response: editForm.response.trim() || null
      })
      .eq("id", requestId);

    if (error) {
      setMessage(error.message);
      return;
    }

    setEditingId(null);
    setMessage("Atendimento atualizado com sucesso.");
    await loadRequests();
  }

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando atendimentos...</section>;
  }

  const openCount = requests.filter((item) =>
    ["open", "in_progress"].includes(item.status)
  ).length;

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">SUPORTE ADMINISTRATIVO · CICLO 18</span>
        <h1>Atendimentos da plataforma</h1>
        <p>
          Acompanhe solicitações das organizações, priorize casos e registre o retorno dado ao usuário.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel support-admin-summary">
        <div>
          <small>FILA ATUAL</small>
          <h2>{openCount} atendimento{openCount === 1 ? "" : "s"} em aberto</h2>
        </div>
        <span>{requests.length} solicitação(ões) no histórico</span>
      </section>

      <div className="support-list">
        {requests.length > 0 ? (
          requests.map((request) => {
            const orgRelation = request.organizations;
            const organization = Array.isArray(orgRelation)
              ? orgRelation[0]
              : orgRelation;
            const profileRelation = request.profiles;
            const profile = Array.isArray(profileRelation)
              ? profileRelation[0]
              : profileRelation;
            const editing = editingId === request.id;

            return (
              <section className="panel support-card" key={request.id}>
                <div className="support-card-heading">
                  <div>
                    <strong>{request.subject}</strong>
                    <span>
                      {organization?.name ?? "Organização"} ·{" "}
                      {profile?.full_name ?? "Usuário"} ·{" "}
                      {new Date(request.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <span className="status-pill">
                    {translateSupportStatus(request.status)}
                  </span>
                </div>

                <div className="support-meta">
                  <span>{translateSupportCategory(request.category)}</span>
                  <span>Prioridade {translateSupportPriority(request.priority)}</span>
                </div>

                <p>{request.message}</p>

                {editing ? (
                  <div className="support-admin-edit">
                    <label>
                      Status
                      <select
                        value={editForm.status}
                        onChange={(event) =>
                          setEditForm({
                            ...editForm,
                            status: event.target.value
                          })
                        }
                      >
                        <option value="open">Aberto</option>
                        <option value="in_progress">Em atendimento</option>
                        <option value="resolved">Resolvido</option>
                        <option value="closed">Encerrado</option>
                      </select>
                    </label>

                    <label>
                      Prioridade
                      <select
                        value={editForm.priority}
                        onChange={(event) =>
                          setEditForm({
                            ...editForm,
                            priority: event.target.value
                          })
                        }
                      >
                        <option value="low">Baixa</option>
                        <option value="normal">Normal</option>
                        <option value="high">Alta</option>
                        <option value="urgent">Urgente</option>
                      </select>
                    </label>

                    <label className="support-form-wide">
                      Resposta
                      <textarea
                        rows={4}
                        value={editForm.response}
                        onChange={(event) =>
                          setEditForm({
                            ...editForm,
                            response: event.target.value
                          })
                        }
                        placeholder="Registre a orientação enviada ao usuário."
                      />
                    </label>

                    <div className="catalog-card-actions">
                      <button
                        className="button button-primary button-small"
                        type="button"
                        onClick={() => void saveSupportRequest(request.id)}
                      >
                        Salvar atendimento
                      </button>
                      <button
                        className="button button-secondary button-small"
                        type="button"
                        onClick={() => setEditingId(null)}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="support-admin-footer">
                    <div>
                      {request.admin_response ? (
                        <>
                          <small>RESPOSTA REGISTRADA</small>
                          <p>{request.admin_response}</p>
                        </>
                      ) : (
                        <small>Sem resposta registrada.</small>
                      )}
                    </div>
                    <button
                      className="button button-secondary button-small"
                      type="button"
                      onClick={() => startSupportEdit(request)}
                    >
                      Atender
                    </button>
                  </div>
                )}
              </section>
            );
          })
        ) : (
          <section className="panel empty-state">
            <strong>Nenhuma solicitação de suporte.</strong>
            <p>A fila está vazia.</p>
          </section>
        )}
      </div>
    </div>
  );
}

type UserProfilePreferences = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  timezone: string;
  locale: string;
  notify_in_app: boolean;
  notify_email: boolean;
};

function ProfilePage() {
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    timezone: "America/Sao_Paulo",
    locale: "pt-BR",
    notifyInApp: true,
    notifyEmail: true
  });
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirmPassword: ""
  });
  const [passwordBusy, setPasswordBusy] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadProfile() {
      const {
        data: { user },
        error: userError
      } = await client.auth.getUser();

      if (!active) return;

      if (userError || !user) {
        setMessage(userError?.message ?? "Não foi possível carregar sua conta.");
        setLoading(false);
        return;
      }

      setEmail(user.email ?? "");

      const { data: profile, error: profileError } = await client
        .from("profiles")
        .select(
          "id, full_name, avatar_url, phone, timezone, locale, notify_in_app, notify_email"
        )
        .eq("id", user.id)
        .single();

      if (!active) return;

      if (profileError) {
        setMessage(profileError.message);
        setLoading(false);
        return;
      }

      const preferences = profile as UserProfilePreferences;

      setAvatarUrl(preferences.avatar_url ?? null);
      setForm({
        fullName: preferences.full_name ?? "",
        phone: preferences.phone ?? "",
        timezone: preferences.timezone || "America/Sao_Paulo",
        locale: preferences.locale || "pt-BR",
        notifyInApp: preferences.notify_in_app,
        notifyEmail: preferences.notify_email
      });
      setLoading(false);
    }

    void loadProfile();

    return () => {
      active = false;
    };
  }, []);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();

    if (!supabase) return;

    const {
      data: { user }
    } = await supabase.auth.getUser();

    if (!user) {
      setMessage("Sua sessão expirou. Entre novamente.");
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: form.fullName.trim() || null,
        phone: form.phone.trim() || null,
        timezone: form.timezone,
        locale: form.locale,
        notify_in_app: form.notifyInApp,
        notify_email: form.notifyEmail,
        updated_at: new Date().toISOString()
      })
      .eq("id", user.id);

    if (error) {
      setMessage(error.message);
      return;
    }

    const { error: authError } = await supabase.auth.updateUser({
      data: {
        full_name: form.fullName.trim()
      }
    });

    if (authError) {
      setMessage(authError.message);
      return;
    }

    window.dispatchEvent(new Event("horizonte:profile-changed"));
    setMessage("Perfil e preferências atualizados com sucesso.");
  }

  async function uploadAvatar(file: File) {
    if (!supabase) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessage("Use uma imagem JPG, PNG ou WebP.");
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setMessage("A foto precisa ter no máximo 3 MB.");
      return;
    }

    setAvatarBusy(true);
    setMessage("Enviando foto...");

    try {
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();

      if (userError || !user) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const objectPath = user.id + "/avatar";

      const { error: uploadError } = await supabase.storage
        .from("profile-avatars")
        .upload(objectPath, file, {
          cacheControl: "3600",
          contentType: file.type,
          upsert: true
        });

      if (uploadError) {
        setMessage(uploadError.message);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from("profile-avatars")
        .getPublicUrl(objectPath);

      const nextAvatarUrl =
        publicUrlData.publicUrl + "?v=" + Date.now();

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          avatar_url: nextAvatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);

      if (profileError) {
        setMessage(profileError.message);
        return;
      }

      setAvatarUrl(nextAvatarUrl);
      window.dispatchEvent(new Event("horizonte:profile-changed"));
      setMessage("Foto de perfil atualizada com sucesso.");
    } finally {
      setAvatarBusy(false);
    }
  }

  async function removeAvatar() {
    if (!supabase || !avatarUrl) return;

    const confirmed = window.confirm("Remover sua foto de perfil?");
    if (!confirmed) return;

    setAvatarBusy(true);

    try {
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();

      if (userError || !user) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const objectPath = user.id + "/avatar";

      const { error: removeError } = await supabase.storage
        .from("profile-avatars")
        .remove([objectPath]);

      if (removeError) {
        setMessage(removeError.message);
        return;
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          avatar_url: null,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);

      if (profileError) {
        setMessage(profileError.message);
        return;
      }

      setAvatarUrl(null);
      window.dispatchEvent(new Event("horizonte:profile-changed"));
      setMessage("Foto de perfil removida.");
    } finally {
      setAvatarBusy(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();

    if (!supabase) return;

    if (passwordForm.password.length < 8) {
      setMessage("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setMessage("As senhas não coincidem.");
      return;
    }

    setPasswordBusy(true);

    const { error } = await supabase.auth.updateUser({
      password: passwordForm.password
    });

    setPasswordBusy(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    setPasswordForm({
      password: "",
      confirmPassword: ""
    });
    setMessage("Senha atualizada com sucesso.");
  }

  async function terminateAllSessions() {
    if (!supabase) return;

    const confirmed = window.confirm(
      "Encerrar sua sessão em todos os dispositivos? Você precisará entrar novamente."
    );

    if (!confirmed) return;

    const { error } = await supabase.auth.signOut({ scope: "global" });

    if (error) {
      setMessage(error.message);
      return;
    }

    navigate("/login");
  }

  if (loading) {
    return <section className="panel">Carregando perfil...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">MEU PERFIL · CICLO 11</span>
        <h1>Perfil e preferências</h1>
        <p>
          Atualize seus dados pessoais, preferências de notificação e segurança da conta.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel profile-summary">
        <div className="profile-avatar-large" aria-hidden="true">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" />
          ) : (
            (form.fullName || email || "U").trim().charAt(0).toUpperCase()
          )}
        </div>

        <div className="profile-summary-copy">
          <small>CONTA ATUAL</small>
          <h2>{form.fullName || "Usuário Horizonte"}</h2>
          <p>{email}</p>
          <p>
            {workspace.organizationName ?? "Organização"} ·{" "}
            <strong>{translateRole(workspace.role)}</strong>
          </p>

          <div className="profile-avatar-actions">
            <label className="button button-secondary button-small profile-avatar-upload">
              {avatarBusy ? "Enviando..." : avatarUrl ? "Trocar foto" : "Adicionar foto"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={avatarBusy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadAvatar(file);
                  event.currentTarget.value = "";
                }}
              />
            </label>

            {avatarUrl ? (
              <button
                className="text-button text-button-danger"
                type="button"
                disabled={avatarBusy}
                onClick={() => void removeAvatar()}
              >
                Remover foto
              </button>
            ) : null}
          </div>

          <small className="profile-avatar-help">
            JPG, PNG ou WebP · até 3 MB
          </small>
        </div>
      </section>

      <section className="panel admin-plan-form">
        <small>DADOS PESSOAIS</small>
        <h2>Informações do perfil</h2>

        <form className="profile-form-grid" onSubmit={saveProfile}>
          <label>
            Nome completo
            <input
              required
              value={form.fullName}
              onChange={(event) =>
                setForm({ ...form, fullName: event.target.value })
              }
              placeholder="Seu nome completo"
            />
          </label>

          <label>
            E-mail
            <input value={email} disabled />
          </label>

          <label>
            Telefone
            <input
              value={form.phone}
              onChange={(event) =>
                setForm({ ...form, phone: event.target.value })
              }
              placeholder="(31) 99999-9999"
            />
          </label>

          <label>
            Fuso horário
            <select
              value={form.timezone}
              onChange={(event) =>
                setForm({ ...form, timezone: event.target.value })
              }
            >
              <option value="America/Sao_Paulo">Brasília / São Paulo</option>
              <option value="America/Recife">Recife</option>
              <option value="America/Fortaleza">Fortaleza</option>
              <option value="America/Manaus">Manaus</option>
              <option value="America/Cuiaba">Cuiabá</option>
              <option value="America/Belem">Belém</option>
              <option value="America/Porto_Velho">Porto Velho</option>
              <option value="America/Rio_Branco">Rio Branco</option>
            </select>
          </label>

          <label>
            Idioma
            <select
              value={form.locale}
              onChange={(event) =>
                setForm({ ...form, locale: event.target.value })
              }
            >
              <option value="pt-BR">Português (Brasil)</option>
            </select>
          </label>

          <div className="profile-preferences">
            <span>Preferências de notificação</span>

            <label className="preference-check">
              <input
                type="checkbox"
                checked={form.notifyInApp}
                onChange={(event) =>
                  setForm({
                    ...form,
                    notifyInApp: event.target.checked
                  })
                }
              />
              <span>Receber notificações dentro da plataforma</span>
            </label>

            <label className="preference-check">
              <input
                type="checkbox"
                checked={form.notifyEmail}
                onChange={(event) =>
                  setForm({
                    ...form,
                    notifyEmail: event.target.checked
                  })
                }
              />
              <span>Permitir notificações por e-mail</span>
            </label>
          </div>

          <button className="button button-primary" type="submit">
            Salvar perfil
          </button>
        </form>
      </section>

      <section className="panel admin-plan-form">
        <small>SEGURANÇA</small>
        <h2>Alterar senha</h2>
        <p>Use pelo menos 8 caracteres para sua nova senha.</p>

        <form className="profile-form-grid" onSubmit={changePassword}>
          <label>
            Nova senha
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={passwordForm.password}
              onChange={(event) =>
                setPasswordForm({
                  ...passwordForm,
                  password: event.target.value
                })
              }
            />
          </label>

          <label>
            Confirmar nova senha
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={passwordForm.confirmPassword}
              onChange={(event) =>
                setPasswordForm({
                  ...passwordForm,
                  confirmPassword: event.target.value
                })
              }
            />
          </label>

          <button
            className="button button-secondary"
            type="submit"
            disabled={passwordBusy}
          >
            {passwordBusy ? "Atualizando..." : "Alterar senha"}
          </button>
        </form>

        <div className="profile-security-extra">
          <div>
            <strong>Encerrar sessões em todos os dispositivos</strong>
            <p>
              Use esta opção se suspeitar de um acesso indevido ou tiver usado um dispositivo compartilhado.
            </p>
          </div>
          <button
            className="button button-secondary"
            type="button"
            onClick={() => void terminateAllSessions()}
          >
            Encerrar todas as sessões
          </button>
        </div>
      </section>
    </div>
  );
}

function SettingsPage() {
  const workspace = useWorkspace();
  const [message, setMessage] = useState("");
  const [billingForm, setBillingForm] = useState({
    email: workspace.billingEmail ?? "",
    cpfCnpj: workspace.billingCpfCnpj ?? "",
    phone: workspace.billingPhone ?? ""
  });

  useEffect(() => {
    setBillingForm({
      email: workspace.billingEmail ?? "",
      cpfCnpj: workspace.billingCpfCnpj ?? "",
      phone: workspace.billingPhone ?? ""
    });
  }, [
    workspace.billingEmail,
    workspace.billingCpfCnpj,
    workspace.billingPhone
  ]);

  async function saveBillingData(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.organizationId) return;

    if (!["owner", "admin"].includes(workspace.role ?? "")) {
      setMessage("Somente proprietária ou administradora pode alterar dados de cobrança.");
      return;
    }

    const { error } = await supabase
      .from("organizations")
      .update({
        billing_email: billingForm.email.trim() || null,
        billing_cpf_cnpj: billingForm.cpfCnpj.trim() || null,
        billing_phone: billingForm.phone.trim() || null
      })
      .eq("id", workspace.organizationId);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Dados de cobrança salvos com sucesso.");
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">CONFIGURAÇÕES</span>
        <h1>Organização</h1>
        <p>Dados reais da organização vinculada à sua conta.</p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel">
        <small>DADOS DA ORGANIZAÇÃO</small>
        <h2>{workspace.organizationName ?? "Organização"}</h2>
        <p>
          Identificador: <strong>{workspace.organizationSlug ?? "ainda não disponível"}</strong>
        </p>
        <p>
          Seu nível de acesso: <strong>{translateRole(workspace.role)}</strong>
        </p>
      </section>

      <section className="panel admin-plan-form">
        <small>DADOS DE COBRANÇA</small>
        <h2>Cadastro para emissão no Asaas</h2>
        <p>Preencha estes dados antes de gerar cobranças reais no Sandbox.</p>

        <form className="plan-form-grid" onSubmit={saveBillingData}>
          <label>
            E-mail de cobrança
            <input
              type="email"
              value={billingForm.email}
              onChange={(event) =>
                setBillingForm({ ...billingForm, email: event.target.value })
              }
              placeholder="financeiro@empresa.com.br"
            />
          </label>

          <label>
            CPF/CNPJ
            <input
              value={billingForm.cpfCnpj}
              onChange={(event) =>
                setBillingForm({ ...billingForm, cpfCnpj: event.target.value })
              }
              placeholder="Somente números ou formatado"
            />
          </label>

          <label>
            Telefone
            <input
              value={billingForm.phone}
              onChange={(event) =>
                setBillingForm({ ...billingForm, phone: event.target.value })
              }
              placeholder="(31) 99999-9999"
            />
          </label>

          <button className="button button-primary" type="submit">
            Salvar dados de cobrança
          </button>
        </form>
      </section>
    </div>
  );
}

type SaaSPlan = {
  id: string;
  product_id: string;
  plan_key: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  billing_cycle: string;
  trial_days: number;
  is_active: boolean;
  products:
    | { product_key: string; name: string }
    | Array<{ product_key: string; name: string }>
    | null;
};

type SaaSPayment = {
  id: string;
  organization_id: string;
  subscription_id: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  provider: string | null;
  provider_payment_id: string | null;
  provider_status: string | null;
  invoice_url: string | null;
  payment_method: string | null;
  organizations:
    | { name: string }
    | Array<{ name: string }>
    | null;
};

type SaaSSubscription = {
  id: string;
  organization_id: string;
  product_id: string;
  plan_id: string;
  status: string;
  starts_at: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  organizations:
    | { name: string }
    | Array<{ name: string }>
    | null;
  products:
    | { name: string }
    | Array<{ name: string }>
    | null;
  plans:
    | { name: string; plan_key: string }
    | Array<{ name: string; plan_key: string }>
    | null;
};

type AdminOrganization = {
  id: string;
  name: string;
  slug: string;
};

type AdminProduct = {
  id: string;
  product_key: string;
  name: string;
};

type AdminAccess = {
  organization_id: string;
  product_id: string;
  access_status: string;
  plan_key: string | null;
};

type PlatformActivityRow = {
  id: string;
  title: string;
  description: string | null;
  event_type: string;
  created_at: string;
  organizations:
    | { name: string }
    | Array<{ name: string }>
    | null;
};

function AdminDashboardPage() {
  const workspace = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [metrics, setMetrics] = useState({
    organizations: 0,
    users: 0,
    activeSubscriptions: 0,
    trialSubscriptions: 0,
    pendingPayments: 0,
    paidAmountCents: 0
  });
  const [recentActivity, setRecentActivity] = useState<PlatformActivityRow[]>([]);

  useEffect(() => {
    if (!supabase || !workspace.isPlatformAdmin) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadDashboard() {
      setLoading(true);
      setMessage("");

      const [
        organizationsResult,
        profilesResult,
        subscriptionsResult,
        paymentsResult,
        activityResult
      ] = await Promise.all([
        client
          .from("organizations")
          .select("id", { count: "exact", head: true }),
        client
          .from("profiles")
          .select("id", { count: "exact", head: true }),
        client
          .from("subscriptions")
          .select("id, status"),
        client
          .from("payments")
          .select("id, amount_cents, status"),
        client
          .from("activity_events")
          .select(
            "id, title, description, event_type, created_at, organizations(name)"
          )
          .order("created_at", { ascending: false })
          .limit(8)
      ]);

      if (!active) return;

      const firstError =
        organizationsResult.error ||
        profilesResult.error ||
        subscriptionsResult.error ||
        paymentsResult.error ||
        activityResult.error;

      if (firstError) {
        setMessage(firstError.message);
        setLoading(false);
        return;
      }

      const subscriptions = subscriptionsResult.data ?? [];
      const payments = paymentsResult.data ?? [];

      setMetrics({
        organizations: organizationsResult.count ?? 0,
        users: profilesResult.count ?? 0,
        activeSubscriptions: subscriptions.filter(
          (item) => item.status === "active"
        ).length,
        trialSubscriptions: subscriptions.filter(
          (item) => item.status === "trialing"
        ).length,
        pendingPayments: payments.filter(
          (item) => item.status === "pending"
        ).length,
        paidAmountCents: payments
          .filter((item) => item.status === "paid")
          .reduce((total, item) => total + (item.amount_cents ?? 0), 0)
      });

      setRecentActivity(
        (activityResult.data ?? []) as PlatformActivityRow[]
      );
      setLoading(false);
    }

    void loadDashboard();

    return () => {
      active = false;
    };
  }, [workspace.isPlatformAdmin]);

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando painel da Horizonte...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">PAINEL HORIZONTE · CICLO 12</span>
        <h1>Visão geral da plataforma</h1>
        <p>
          Acompanhe organizações, usuários, assinaturas, cobranças e os acontecimentos
          mais recentes do ecossistema Horizonte.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <div className="platform-metrics-grid">
        <section className="panel platform-metric-card">
          <small>ORGANIZAÇÕES</small>
          <strong>{metrics.organizations}</strong>
          <span>contas empresariais cadastradas</span>
        </section>

        <section className="panel platform-metric-card">
          <small>USUÁRIOS</small>
          <strong>{metrics.users}</strong>
          <span>perfis cadastrados na plataforma</span>
        </section>

        <section className="panel platform-metric-card">
          <small>ASSINATURAS ATIVAS</small>
          <strong>{metrics.activeSubscriptions}</strong>
          <span>{metrics.trialSubscriptions} em período de teste</span>
        </section>

        <section className="panel platform-metric-card">
          <small>RECEITA CONFIRMADA</small>
          <strong>{formatPlanPrice(metrics.paidAmountCents)}</strong>
          <span>{metrics.pendingPayments} cobrança(s) pendente(s)</span>
        </section>
      </div>

      <div className="platform-dashboard-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <small>ATALHOS DE GESTÃO</small>
              <h2>Administração</h2>
              <p>Acesse rapidamente as áreas principais da operação.</p>
            </div>
          </div>

          <div className="platform-quick-links">
            <Link to="/admin">Organizações e acessos</Link>
            <Link to="/admin/sistemas">Sistemas da plataforma</Link>
            <Link to="/admin/planos">Planos e assinaturas</Link>
            <Link to="/admin/pagamentos">Cobranças</Link>
            <Link to="/admin/suporte">Suporte administrativo</Link>
            <Link to="/admin/lancamento">Lançamento v1.0</Link>
          </div>
        </section>

        <section className="panel">
          <div className="panel-heading">
            <div>
              <small>ATIVIDADE RECENTE</small>
              <h2>Últimos acontecimentos</h2>
            </div>
          </div>

          {recentActivity.length === 0 ? (
            <div className="empty-state compact-empty">
              <p>Ainda não há atividades registradas.</p>
            </div>
          ) : (
            <div className="platform-activity-list">
              {recentActivity.map((activity) => {
                const organizationRelation = activity.organizations;
                const organization = Array.isArray(organizationRelation)
                  ? organizationRelation[0]
                  : organizationRelation;

                return (
                  <article key={activity.id}>
                    <div>
                      <strong>{activity.title}</strong>
                      <span>
                        {organization?.name ?? "Organização"} ·{" "}
                        {new Date(activity.created_at).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    {activity.description && <p>{activity.description}</p>}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

type LaunchReadinessCheck = {
  key: string;
  title: string;
  detail: string;
  status: "ok" | "attention" | "checking";
};

function AdminLaunchPage() {
  const workspace = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [gatewayEnvironment, setGatewayEnvironment] =
    useState<"sandbox" | "production">("sandbox");
  const [checks, setChecks] = useState<LaunchReadinessCheck[]>([]);

  useEffect(() => {
    if (!supabase || !workspace.isPlatformAdmin) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadReadiness() {
      setLoading(true);
      setMessage("");

      const [
        organizationsResult,
        productsResult,
        subscriptionsResult,
        paymentsResult,
        supportResult
      ] = await Promise.all([
        client.from("organizations").select("id", { count: "exact", head: true }),
        client
          .from("products")
          .select("id, status, launch_url, name")
          .order("name"),
        client.from("subscriptions").select("id, status"),
        client.from("payments").select("id, status"),
        client
          .from("support_requests")
          .select("id", { count: "exact", head: true })
      ]);

      if (!active) return;

      const nextChecks: LaunchReadinessCheck[] = [];

      nextChecks.push({
        key: "organizations",
        title: "Organizações e autenticação",
        status:
          !organizationsResult.error && (organizationsResult.count ?? 0) > 0
            ? "ok"
            : "attention",
        detail: organizationsResult.error
          ? organizationsResult.error.message
          : (organizationsResult.count ?? 0) > 0
            ? `${organizationsResult.count} organização(ões) cadastrada(s).`
            : "Nenhuma organização cadastrada."
      });

      const productRows = productsResult.data ?? [];
      const activeProducts = productRows.filter(
        (product) => product.status === "active"
      );
      const launchConfigured = productRows.filter(
        (product) => Boolean(product.launch_url)
      );

      nextChecks.push({
        key: "catalog",
        title: "Catálogo de sistemas",
        status:
          !productsResult.error && productRows.length > 0 ? "ok" : "attention",
        detail: productsResult.error
          ? productsResult.error.message
          : `${productRows.length} sistema(s) cadastrado(s); ${activeProducts.length} ativo(s).`
      });

      nextChecks.push({
        key: "launch_urls",
        title: "URLs de abertura dos produtos",
        status:
          activeProducts.length === 0 ||
          activeProducts.every((product) => Boolean(product.launch_url))
            ? "ok"
            : "attention",
        detail:
          activeProducts.length === 0
            ? `${launchConfigured.length} URL(s) configurada(s). Os produtos ainda não estão marcados como Ativos.`
            : `${launchConfigured.length} de ${productRows.length} produto(s) possuem URL de acesso.`
      });

      const subscriptionRows = subscriptionsResult.data ?? [];
      const liveSubscriptions = subscriptionRows.filter((subscription) =>
        ["active", "trialing"].includes(subscription.status)
      );

      nextChecks.push({
        key: "subscriptions",
        title: "Assinaturas e acessos",
        status:
          !subscriptionsResult.error && liveSubscriptions.length > 0
            ? "ok"
            : "attention",
        detail: subscriptionsResult.error
          ? subscriptionsResult.error.message
          : `${liveSubscriptions.length} assinatura(s) ativa(s) ou em teste.`
      });

      const paymentRows = paymentsResult.data ?? [];
      const paidPayments = paymentRows.filter(
        (payment) => payment.status === "paid"
      );

      nextChecks.push({
        key: "billing",
        title: "Cobrança ponta a ponta",
        status:
          !paymentsResult.error && paidPayments.length > 0 ? "ok" : "attention",
        detail: paymentsResult.error
          ? paymentsResult.error.message
          : `${paidPayments.length} cobrança(s) confirmada(s) como paga(s).`
      });

      nextChecks.push({
        key: "support",
        title: "Suporte e segurança",
        status: supportResult.error ? "attention" : "ok",
        detail: supportResult.error
          ? "Execute a migration 018 no Supabase para ativar a Central de suporte."
          : "Central de suporte disponível e protegida por RLS."
      });

      try {
        const {
          data: { session }
        } = await client.auth.getSession();

        if (!session?.access_token) {
          throw new Error("Sessão administrativa expirada.");
        }

        const response = await fetch("/api/asaas/status", {
          headers: {
            Authorization: "Bearer " + session.access_token
          }
        });

        const responseText = await response.text();
        let gateway:
          | {
              ok?: boolean;
              environment?: "sandbox" | "production";
              message?: string;
              error?: string;
            }
          | null = null;

        if (responseText) {
          try {
            gateway = JSON.parse(responseText) as {
              ok?: boolean;
              environment?: "sandbox" | "production";
              message?: string;
              error?: string;
            };
          } catch {
            gateway = null;
          }
        }

        if (gateway?.environment) {
          setGatewayEnvironment(gateway.environment);
        }

        nextChecks.push({
          key: "gateway",
          title: "Gateway Asaas",
          status: response.ok && gateway?.ok ? "ok" : "attention",
          detail:
            gateway?.message ||
            gateway?.error ||
            "Não foi possível validar o gateway."
        });
      } catch (error) {
        nextChecks.push({
          key: "gateway",
          title: "Gateway Asaas",
          status: "attention",
          detail:
            error instanceof Error
              ? error.message
              : "Não foi possível validar o gateway."
        });
      }

      if (!active) return;

      setChecks(nextChecks);
      setLoading(false);
    }

    void loadReadiness();

    return () => {
      active = false;
    };
  }, [workspace.isPlatformAdmin]);

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Preparando checklist de lançamento...</section>;
  }

  const readyCount = checks.filter((check) => check.status === "ok").length;
  const attentionCount = checks.filter(
    (check) => check.status === "attention"
  ).length;

  return (
    <div>
      <div className="page-title launch-title">
        <div>
          <span className="eyebrow">GRANDE FINAL · CICLO 19</span>
          <h1>Central de lançamento v1.0</h1>
          <p>
            Visão final da fundação SaaS da Horizonte dos Sistemas antes do
            início da próxima temporada.
          </p>
        </div>
        <span className="launch-version">v1.0.0</span>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel launch-summary">
        <div>
          <small>TEMPORADA 1</small>
          <h2>
            {attentionCount === 0
              ? "Fundação pronta para lançamento"
              : "Fundação concluída com pendências de ambiente"}
          </h2>
          <p>
            {readyCount} de {checks.length} verificações estão prontas.
          </p>
        </div>

        <div className="launch-score">
          <strong>{readyCount}/{checks.length}</strong>
          <span>itens prontos</span>
        </div>
      </section>

      <div className="launch-check-grid">
        {checks.map((check) => (
          <section
            className={
              "panel launch-check-card launch-check-" + check.status
            }
            key={check.key}
          >
            <div className="launch-check-heading">
              <strong>{check.title}</strong>
              <span className="status-pill">
                {check.status === "ok" ? "Pronto" : "Atenção"}
              </span>
            </div>
            <p>{check.detail}</p>
          </section>
        ))}
      </div>

      <section className="panel launch-next-season">
        <small>PRÓXIMA TEMPORADA</small>
        <h2>Os produtos passam a ser as estrelas</h2>
        <p>
          A plataforma-base está estruturada. Daqui em diante, Central de
          Projetos, Verbum, AutoCare e os próximos sistemas podem evoluir como
          produtos independentes dentro do ecossistema Horizonte.
        </p>

        <div className="launch-actions">
          <Link className="button button-secondary" to="/admin/sistemas">
            Revisar sistemas
          </Link>
          <Link className="button button-secondary" to="/admin/pagamentos">
            Revisar cobranças
          </Link>
          <Link className="button button-secondary" to="/admin/suporte">
            Revisar suporte
          </Link>
        </div>
      </section>
    </div>
  );
}

type AdminCatalogProduct = {
  id: string;
  product_key: string;
  name: string;
  description: string | null;
  status: string;
  launch_url: string | null;
  created_at: string;
};

function AdminProductsPage() {
  const workspace = useWorkspace();
  const [products, setProducts] = useState<AdminCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    productKey: "",
    name: "",
    description: "",
    launchUrl: "",
    status: "planned"
  });
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    launchUrl: "",
    status: "planned"
  });

  async function loadProducts() {
    if (!supabase || !workspace.isPlatformAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setMessage("");

    const { data, error } = await supabase
      .from("products")
      .select("id, product_key, name, description, status, launch_url, created_at")
      .order("name");

    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }

    setProducts((data ?? []) as AdminCatalogProduct[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadProducts();
  }, [workspace.isPlatformAdmin]);

  function normalizeProductKey(value: string) {
    return value
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  async function createProduct(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.isPlatformAdmin) return;

    const productKey = normalizeProductKey(createForm.productKey || createForm.name);

    if (!productKey) {
      setMessage("Informe um nome ou identificador válido para o sistema.");
      return;
    }

    const { error } = await supabase.from("products").insert({
      product_key: productKey,
      name: createForm.name.trim(),
      description: createForm.description.trim() || null,
      launch_url: createForm.launchUrl.trim() || null,
      status: createForm.status
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    setCreateForm({
      productKey: "",
      name: "",
      description: "",
      launchUrl: "",
      status: "planned"
    });
    setMessage("Sistema cadastrado com sucesso.");
    await loadProducts();
  }

  function startEdit(product: AdminCatalogProduct) {
    setEditingId(product.id);
    setEditForm({
      name: product.name,
      description: product.description ?? "",
      launchUrl: product.launch_url ?? "",
      status: product.status
    });
    setMessage("");
  }

  async function saveEdit(productId: string) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const { error } = await supabase
      .from("products")
      .update({
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        launch_url: editForm.launchUrl.trim() || null,
        status: editForm.status
      })
      .eq("id", productId);

    if (error) {
      setMessage(error.message);
      return;
    }

    setEditingId(null);
    setMessage("Sistema atualizado com sucesso.");
    await loadProducts();
  }

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando catálogo de sistemas...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">CATÁLOGO · CICLO 14</span>
        <h1>Sistemas da plataforma</h1>
        <p>
          Cadastre novos produtos e controle como cada sistema aparece no ecossistema Horizonte.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel admin-plan-form">
        <small>NOVO SISTEMA</small>
        <h2>Cadastrar produto</h2>
        <p>
          O identificador é técnico e deve permanecer estável depois que o sistema começar a ser usado.
        </p>

        <form className="catalog-product-form" onSubmit={createProduct}>
          <label>
            Nome do sistema
            <input
              required
              value={createForm.name}
              onChange={(event) =>
                setCreateForm({ ...createForm, name: event.target.value })
              }
              placeholder="Ex.: Agenda Inteligente"
            />
          </label>

          <label>
            Identificador
            <input
              value={createForm.productKey}
              onChange={(event) =>
                setCreateForm({
                  ...createForm,
                  productKey: normalizeProductKey(event.target.value)
                })
              }
              placeholder="agenda-inteligente"
            />
          </label>

          <label className="catalog-description-field">
            Descrição
            <textarea
              rows={3}
              value={createForm.description}
              onChange={(event) =>
                setCreateForm({
                  ...createForm,
                  description: event.target.value
                })
              }
              placeholder="Descreva a finalidade do sistema."
            />
          </label>

          <label className="catalog-description-field">
            URL de acesso
            <input
              type="url"
              value={createForm.launchUrl}
              onChange={(event) =>
                setCreateForm({
                  ...createForm,
                  launchUrl: event.target.value
                })
              }
              placeholder="https://seusistema.com.br"
            />
          </label>

          <label>
            Status inicial
            <select
              value={createForm.status}
              onChange={(event) =>
                setCreateForm({ ...createForm, status: event.target.value })
              }
            >
              <option value="planned">Planejado</option>
              <option value="development">Em desenvolvimento</option>
              <option value="active">Ativo</option>
              <option value="paused">Pausado</option>
            </select>
          </label>

          <button className="button button-primary" type="submit">
            Cadastrar sistema
          </button>
        </form>
      </section>

      <div className="catalog-admin-grid">
        {products.map((product) => {
          const editing = editingId === product.id;

          return (
            <section className="panel catalog-admin-card" key={product.id}>
              <div className="catalog-admin-card-head">
                <div>
                  <small>{product.product_key}</small>
                  <h2>{product.name}</h2>
                </div>
                <span className="status-pill">
                  {translateProductStatus(product.status)}
                </span>
              </div>

              {editing ? (
                <div className="catalog-edit-form">
                  <label>
                    Nome
                    <input
                      value={editForm.name}
                      onChange={(event) =>
                        setEditForm({ ...editForm, name: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Descrição
                    <textarea
                      rows={4}
                      value={editForm.description}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          description: event.target.value
                        })
                      }
                    />
                  </label>

                  <label>
                    URL de acesso
                    <input
                      type="url"
                      value={editForm.launchUrl}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          launchUrl: event.target.value
                        })
                      }
                      placeholder="https://seusistema.com.br"
                    />
                  </label>

                  <label>
                    Status
                    <select
                      value={editForm.status}
                      onChange={(event) =>
                        setEditForm({ ...editForm, status: event.target.value })
                      }
                    >
                      <option value="planned">Planejado</option>
                      <option value="development">Em desenvolvimento</option>
                      <option value="active">Ativo</option>
                      <option value="paused">Pausado</option>
                    </select>
                  </label>

                  <div className="catalog-card-actions">
                    <button
                      className="button button-primary button-small"
                      type="button"
                      onClick={() => void saveEdit(product.id)}
                    >
                      Salvar
                    </button>
                    <button
                      className="button button-secondary button-small"
                      type="button"
                      onClick={() => setEditingId(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p>{product.description || "Sem descrição cadastrada."}</p>
                  <div className="catalog-card-footer">
                    <span>
                      Criado em{" "}
                      {new Date(product.created_at).toLocaleDateString("pt-BR")}
                    </span>
                    <button
                      className="button button-secondary button-small"
                      type="button"
                      onClick={() => startEdit(product)}
                    >
                      Editar sistema
                    </button>
                  </div>
                </>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

type AdminOrganizationDetail = {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  billing_email: string | null;
  billing_cpf_cnpj: string | null;
  billing_phone: string | null;
  created_at: string;
};

type AdminOrganizationMemberDetail = {
  user_id: string;
  role: string;
  created_at: string;
  profileName: string | null;
};

type AdminOrganizationAccessDetail = {
  product_id: string;
  access_status: string;
  plan_key: string | null;
  products:
    | { name: string; product_key: string }
    | Array<{ name: string; product_key: string }>
    | null;
};

type AdminOrganizationSubscriptionDetail = {
  id: string;
  status: string;
  starts_at: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  products:
    | { name: string }
    | Array<{ name: string }>
    | null;
  plans:
    | { name: string; plan_key: string }
    | Array<{ name: string; plan_key: string }>
    | null;
};

type AdminOrganizationPaymentDetail = {
  id: string;
  amount_cents: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  payment_method: string | null;
  invoice_url: string | null;
  created_at: string;
};

function AdminOrganizationDetailPage() {
  const workspace = useWorkspace();
  const { organizationId } = useParams();
  const [organization, setOrganization] =
    useState<AdminOrganizationDetail | null>(null);
  const [members, setMembers] =
    useState<AdminOrganizationMemberDetail[]>([]);
  const [accesses, setAccesses] =
    useState<AdminOrganizationAccessDetail[]>([]);
  const [subscriptions, setSubscriptions] =
    useState<AdminOrganizationSubscriptionDetail[]>([]);
  const [payments, setPayments] =
    useState<AdminOrganizationPaymentDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supabase || !workspace.isPlatformAdmin || !organizationId) {
      setLoading(false);
      return;
    }

    const client = supabase;
    let active = true;

    async function loadOrganizationDetail() {
      setLoading(true);
      setMessage("");

      const [
        organizationResult,
        membersResult,
        accessResult,
        subscriptionsResult,
        paymentsResult
      ] = await Promise.all([
        client
          .from("organizations")
          .select(
            "id, name, slug, owner_id, billing_email, billing_cpf_cnpj, billing_phone, created_at"
          )
          .eq("id", organizationId)
          .maybeSingle(),
        client
          .from("organization_members")
          .select("user_id, role, created_at")
          .eq("organization_id", organizationId)
          .order("created_at", { ascending: true }),
        client
          .from("organization_products")
          .select(
            "product_id, access_status, plan_key, products(name, product_key)"
          )
          .eq("organization_id", organizationId),
        client
          .from("subscriptions")
          .select(
            "id, status, starts_at, trial_ends_at, current_period_end, products(name), plans(name, plan_key)"
          )
          .eq("organization_id", organizationId)
          .order("starts_at", { ascending: false }),
        client
          .from("payments")
          .select(
            "id, amount_cents, currency, status, due_date, paid_at, payment_method, invoice_url, created_at"
          )
          .eq("organization_id", organizationId)
          .order("created_at", { ascending: false })
          .limit(10)
      ]);

      if (!active) return;

      const firstError =
        organizationResult.error ||
        membersResult.error ||
        accessResult.error ||
        subscriptionsResult.error ||
        paymentsResult.error;

      if (firstError) {
        setMessage(firstError.message);
        setLoading(false);
        return;
      }

      const memberRows = membersResult.data ?? [];
      const memberIds = memberRows.map((member) => member.user_id);
      const profileNames = new Map<string, string | null>();

      if (memberIds.length > 0) {
        const profilesResult = await client
          .from("profiles")
          .select("id, full_name")
          .in("id", memberIds);

        if (!active) return;

        if (profilesResult.error) {
          setMessage(profilesResult.error.message);
          setLoading(false);
          return;
        }

        (profilesResult.data ?? []).forEach((profile) => {
          profileNames.set(profile.id, profile.full_name ?? null);
        });
      }

      setOrganization(
        (organizationResult.data as AdminOrganizationDetail | null) ?? null
      );
      setMembers(
        memberRows.map((member) => ({
          user_id: member.user_id,
          role: member.role,
          created_at: member.created_at,
          profileName: profileNames.get(member.user_id) ?? null
        }))
      );
      setAccesses(
        (accessResult.data ?? []) as AdminOrganizationAccessDetail[]
      );
      setSubscriptions(
        (subscriptionsResult.data ?? []) as AdminOrganizationSubscriptionDetail[]
      );
      setPayments(
        (paymentsResult.data ?? []) as AdminOrganizationPaymentDetail[]
      );
      setLoading(false);
    }

    void loadOrganizationDetail();

    return () => {
      active = false;
    };
  }, [workspace.isPlatformAdmin, organizationId]);

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando ficha da organização...</section>;
  }

  if (!organization) {
    return (
      <section className="panel empty-state">
        <strong>Organização não encontrada.</strong>
        <p>{message || "Não foi possível localizar essa organização."}</p>
        <Link className="button button-secondary" to="/admin">
          Voltar para organizações
        </Link>
      </section>
    );
  }

  const activeSystems = accesses.filter(
    (item) => item.access_status === "active" || item.access_status === "trial"
  ).length;

  const activeSubscriptions = subscriptions.filter(
    (item) => item.status === "active" || item.status === "trialing"
  ).length;

  const paidAmount = payments
    .filter((item) => item.status === "paid")
    .reduce((total, item) => total + item.amount_cents, 0);

  return (
    <div>
      <div className="page-title admin-detail-title">
        <div>
          <span className="eyebrow">ORGANIZAÇÃO · CICLO 13</span>
          <h1>{organization.name}</h1>
          <p>
            Ficha administrativa completa da organização dentro da Horizonte.
          </p>
        </div>
        <Link className="button button-secondary" to="/admin">
          ← Voltar
        </Link>
      </div>

      {message && <p className="form-message">{message}</p>}

      <div className="platform-metrics-grid admin-detail-metrics">
        <section className="panel platform-metric-card">
          <small>MEMBROS</small>
          <strong>{members.length}</strong>
          <span>pessoa(s) vinculada(s)</span>
        </section>

        <section className="panel platform-metric-card">
          <small>SISTEMAS LIBERADOS</small>
          <strong>{activeSystems}</strong>
          <span>ativos ou em teste</span>
        </section>

        <section className="panel platform-metric-card">
          <small>ASSINATURAS</small>
          <strong>{activeSubscriptions}</strong>
          <span>ativas ou em teste</span>
        </section>

        <section className="panel platform-metric-card">
          <small>TOTAL PAGO</small>
          <strong>{formatPlanPrice(paidAmount)}</strong>
          <span>nas últimas cobranças exibidas</span>
        </section>
      </div>

      <div className="admin-detail-grid">
        <section className="panel">
          <small>DADOS DA ORGANIZAÇÃO</small>
          <h2>Cadastro</h2>
          <div className="admin-detail-data">
            <div>
              <span>Identificador</span>
              <strong>{organization.slug}</strong>
            </div>
            <div>
              <span>Criada em</span>
              <strong>
                {new Date(organization.created_at).toLocaleDateString("pt-BR")}
              </strong>
            </div>
            <div>
              <span>E-mail de cobrança</span>
              <strong>{organization.billing_email || "Não informado"}</strong>
            </div>
            <div>
              <span>CPF/CNPJ</span>
              <strong>{organization.billing_cpf_cnpj || "Não informado"}</strong>
            </div>
            <div>
              <span>Telefone</span>
              <strong>{organization.billing_phone || "Não informado"}</strong>
            </div>
          </div>
        </section>

        <section className="panel">
          <small>EQUIPE</small>
          <h2>Membros</h2>
          <div className="admin-detail-list">
            {members.map((member) => (
              <article key={member.user_id}>
                <div>
                  <strong>{member.profileName || "Usuário sem nome"}</strong>
                  <span>
                    Desde {new Date(member.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
                <span className="status-pill">{translateRole(member.role)}</span>
              </article>
            ))}
          </div>
        </section>
      </div>

      <section className="panel">
        <small>SISTEMAS E ACESSOS</small>
        <h2>Produtos da organização</h2>
        <div className="admin-detail-list">
          {accesses.length === 0 ? (
            <p>Nenhum sistema vinculado.</p>
          ) : (
            accesses.map((access) => {
              const relation = access.products;
              const product = Array.isArray(relation) ? relation[0] : relation;

              return (
                <article key={access.product_id}>
                  <div>
                    <strong>{product?.name ?? "Sistema"}</strong>
                    <span>{product?.product_key ?? "produto"}</span>
                  </div>
                  <span className="status-pill">
                    {translateAccessStatus(access.access_status)}
                  </span>
                </article>
              );
            })
          )}
        </div>
      </section>

      <div className="admin-detail-grid">
        <section className="panel">
          <small>ASSINATURAS</small>
          <h2>Planos vinculados</h2>
          <div className="admin-detail-list">
            {subscriptions.length === 0 ? (
              <p>Nenhuma assinatura vinculada.</p>
            ) : (
              subscriptions.map((subscription) => {
                const productRelation = subscription.products;
                const product = Array.isArray(productRelation)
                  ? productRelation[0]
                  : productRelation;
                const planRelation = subscription.plans;
                const plan = Array.isArray(planRelation)
                  ? planRelation[0]
                  : planRelation;

                return (
                  <article key={subscription.id}>
                    <div>
                      <strong>{product?.name ?? "Sistema"}</strong>
                      <span>{plan?.name ?? "Plano"}</span>
                    </div>
                    <span className="status-pill">
                      {translateSubscriptionStatus(subscription.status)}
                    </span>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section className="panel">
          <small>COBRANÇAS RECENTES</small>
          <h2>Financeiro</h2>
          <div className="admin-detail-list">
            {payments.length === 0 ? (
              <p>Nenhuma cobrança registrada.</p>
            ) : (
              payments.map((payment) => (
                <article key={payment.id}>
                  <div>
                    <strong>
                      {formatPlanPrice(payment.amount_cents, payment.currency)}
                    </strong>
                    <span>
                      {payment.due_date
                        ? "Vencimento " +
                          new Date(payment.due_date + "T12:00:00").toLocaleDateString(
                            "pt-BR"
                          )
                        : "Sem vencimento"}
                    </span>
                  </div>
                  <span className="status-pill">
                    {translatePaymentStatus(payment.status)}
                  </span>
                </article>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function AdminPage() {
  const workspace = useWorkspace();
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [adminProducts, setAdminProducts] = useState<AdminProduct[]>([]);
  const [accesses, setAccesses] = useState<AdminAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function loadAdminData() {
    if (!supabase || !workspace.isPlatformAdmin) return;

    setLoading(true);
    setMessage("");

    const [orgResult, productResult, accessResult] = await Promise.all([
      supabase.from("organizations").select("id, name, slug").order("name"),
      supabase.from("products").select("id, product_key, name").order("name"),
      supabase
        .from("organization_products")
        .select("organization_id, product_id, access_status, plan_key")
    ]);

    const firstError = orgResult.error || productResult.error || accessResult.error;

    if (firstError) {
      setMessage(firstError.message);
      setLoading(false);
      return;
    }

    setOrganizations((orgResult.data ?? []) as AdminOrganization[]);
    setAdminProducts((productResult.data ?? []) as AdminProduct[]);
    setAccesses((accessResult.data ?? []) as AdminAccess[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadAdminData();
  }, [workspace.isPlatformAdmin]);

  async function updateAccess(
    organizationId: string,
    productId: string,
    nextStatus: string
  ) {
    if (!supabase) return;

    setMessage("");

    const { error } = await supabase
      .from("organization_products")
      .upsert(
        {
          organization_id: organizationId,
          product_id: productId,
          access_status: nextStatus,
          plan_key: nextStatus === "trial" ? "beta" : null
        },
        { onConflict: "organization_id,product_id" }
      );

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Acesso atualizado com sucesso.");
    await loadAdminData();
  }

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da Horizonte dos Sistemas.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando administração...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">ADMINISTRAÇÃO DA PLATAFORMA</span>
        <h1>Organizações e acessos</h1>
        <p>
          Controle quais sistemas cada organização pode utilizar dentro do ecossistema Horizonte.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <div className="admin-org-list">
        {organizations.map((organization) => (
          <section className="panel admin-org-card" key={organization.id}>
            <div className="panel-heading">
              <div>
                <small>ORGANIZAÇÃO</small>
                <h2>{organization.name}</h2>
                <p>{organization.slug}</p>
              </div>
              <Link
                className="button button-secondary button-small"
                to={"/admin/organizacoes/" + organization.id}
              >
                Abrir ficha
              </Link>
            </div>

            <div className="admin-access-grid">
              {adminProducts.map((product) => {
                const current = accesses.find(
                  (item) =>
                    item.organization_id === organization.id &&
                    item.product_id === product.id
                );

                return (
                  <div className="admin-access-row" key={product.id}>
                    <div>
                      <strong>{product.name}</strong>
                      <span>
                        {current
                          ? translateAccessStatus(current.access_status)
                          : "Sem acesso"}
                      </span>
                    </div>

                    <select
                      value={current?.access_status ?? "none"}
                      onChange={(event) => {
                        const value = event.target.value;

                        if (value === "none") return;
                        void updateAccess(organization.id, product.id, value);
                      }}
                    >
                      <option value="none">Sem acesso</option>
                      <option value="trial">Período de teste</option>
                      <option value="active">Acesso ativo</option>
                      <option value="suspended">Suspenso</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function AdminPlansPage() {
  const workspace = useWorkspace();
  const [plans, setPlans] = useState<SaaSPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<SaaSSubscription[]>([]);
  const [productsForPlans, setProductsForPlans] = useState<AdminProduct[]>([]);
  const [organizationsForSubscriptions, setOrganizationsForSubscriptions] = useState<AdminOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    productId: "",
    planKey: "",
    name: "",
    description: "",
    price: "0",
    billingCycle: "monthly",
    trialDays: "0"
  });
  const [subscriptionForm, setSubscriptionForm] = useState({
    organizationId: "",
    planId: "",
    status: "trialing",
    currentPeriodEnd: ""
  });

  async function loadBillingData() {
    if (!supabase || !workspace.isPlatformAdmin) return;

    setLoading(true);

    const [plansResult, subscriptionsResult, productsResult, organizationsResult] = await Promise.all([
      supabase
        .from("plans")
        .select(
          "id, product_id, plan_key, name, description, price_cents, currency, billing_cycle, trial_days, is_active, products(product_key, name)"
        )
        .order("price_cents"),
      supabase
        .from("subscriptions")
        .select(
          "id, organization_id, product_id, plan_id, status, starts_at, trial_ends_at, current_period_end, organizations(name), products(name), plans(name, plan_key)"
        ),
      supabase.from("products").select("id, product_key, name").order("name"),
      supabase.from("organizations").select("id, name, slug").order("name")
    ]);

    const firstError =
      plansResult.error ||
      subscriptionsResult.error ||
      productsResult.error ||
      organizationsResult.error;

    if (firstError) {
      setMessage(firstError.message);
      setLoading(false);
      return;
    }

    setPlans((plansResult.data ?? []) as SaaSPlan[]);
    setSubscriptions((subscriptionsResult.data ?? []) as SaaSSubscription[]);
    setProductsForPlans((productsResult.data ?? []) as AdminProduct[]);
    setOrganizationsForSubscriptions(
      (organizationsResult.data ?? []) as AdminOrganization[]
    );
    setLoading(false);
  }

  useEffect(() => {
    void loadBillingData();
  }, [workspace.isPlatformAdmin]);

  async function createPlan(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.isPlatformAdmin) return;

    const normalizedPrice = Number(
      form.price.replace(".", "").replace(",", ".")
    );

    if (!form.productId || !form.planKey || !form.name || Number.isNaN(normalizedPrice)) {
      setMessage("Preencha produto, chave, nome e preço do plano.");
      return;
    }

    const priceCents = Math.round(normalizedPrice * 100);

    const { error } = await supabase.from("plans").insert({
      product_id: form.productId,
      plan_key: form.planKey.trim().toLowerCase(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_cents: priceCents,
      currency: "BRL",
      billing_cycle: form.billingCycle,
      trial_days: Math.max(0, Number(form.trialDays) || 0),
      is_active: true
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Plano criado com sucesso.");
    setForm({
      productId: "",
      planKey: "",
      name: "",
      description: "",
      price: "0",
      billingCycle: "monthly",
      trialDays: "0"
    });
    await loadBillingData();
  }

  async function saveSubscription(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.isPlatformAdmin) return;

    const selectedPlan = plans.find(
      (plan: SaaSPlan) => plan.id === subscriptionForm.planId
    );

    if (!subscriptionForm.organizationId || !selectedPlan) {
      setMessage("Selecione a organização e o plano.");
      return;
    }

    const currentPeriodEnd = subscriptionForm.currentPeriodEnd
      ? new Date(subscriptionForm.currentPeriodEnd + "T23:59:59").toISOString()
      : null;

    const trialEndsAt =
      subscriptionForm.status === "trialing" && selectedPlan.trial_days > 0
        ? new Date(
            Date.now() + selectedPlan.trial_days * 24 * 60 * 60 * 1000
          ).toISOString()
        : null;

    const { error } = await supabase
      .from("subscriptions")
      .upsert(
        {
          organization_id: subscriptionForm.organizationId,
          product_id: selectedPlan.product_id,
          plan_id: selectedPlan.id,
          status: subscriptionForm.status,
          trial_ends_at: trialEndsAt,
          current_period_end: currentPeriodEnd
        },
        { onConflict: "organization_id,product_id" }
      );

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Assinatura salva com sucesso.");
    setSubscriptionForm({
      organizationId: "",
      planId: "",
      status: "trialing",
      currentPeriodEnd: ""
    });
    await loadBillingData();
  }

  async function updateSubscriptionStatus(
    subscriptionId: string,
    status: string
  ) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const { error } = await supabase
      .from("subscriptions")
      .update({ status })
      .eq("id", subscriptionId);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Status da assinatura atualizado.");
    await loadBillingData();
  }

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da plataforma.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando planos e assinaturas...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">ADMINISTRAÇÃO DE PLANOS</span>
        <h1>Planos e assinaturas</h1>
        <p>
          Cadastre os planos comerciais e acompanhe as assinaturas das organizações.
        </p>
      </div>

      {message && <p className="form-message">{message}</p>}

      <section className="panel admin-plan-form">
        <small>NOVO PLANO</small>
        <h2>Cadastrar plano</h2>
        <form className="plan-form-grid" onSubmit={createPlan}>
          <label>
            Sistema
            <select
              required
              value={form.productId}
              onChange={(event) => setForm({ ...form, productId: event.target.value })}
            >
              <option value="">Selecione</option>
              {productsForPlans.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Chave do plano
            <input
              required
              value={form.planKey}
              onChange={(event) => setForm({ ...form, planKey: event.target.value })}
              placeholder="ex.: essencial"
            />
          </label>

          <label>
            Nome
            <input
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Plano Essencial"
            />
          </label>

          <label>
            Preço em R$
            <input
              required
              value={form.price}
              onChange={(event) => setForm({ ...form, price: event.target.value })}
              inputMode="decimal"
              placeholder="49,90"
            />
          </label>

          <label>
            Ciclo
            <select
              value={form.billingCycle}
              onChange={(event) => setForm({ ...form, billingCycle: event.target.value })}
            >
              <option value="monthly">Mensal</option>
              <option value="yearly">Anual</option>
              <option value="custom">Personalizado</option>
            </select>
          </label>

          <label>
            Dias de teste
            <input
              type="number"
              min="0"
              value={form.trialDays}
              onChange={(event) => setForm({ ...form, trialDays: event.target.value })}
            />
          </label>

          <label className="plan-description-field">
            Descrição
            <input
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="Resumo do que este plano oferece"
            />
          </label>

          <button className="button button-primary" type="submit">
            Criar plano
          </button>
        </form>
      </section>

      <section className="panel admin-plan-form">
        <small>NOVA ASSINATURA</small>
        <h2>Vincular plano a uma organização</h2>

        <form className="plan-form-grid" onSubmit={saveSubscription}>
          <label>
            Organização
            <select
              required
              value={subscriptionForm.organizationId}
              onChange={(event) =>
                setSubscriptionForm({
                  ...subscriptionForm,
                  organizationId: event.target.value
                })
              }
            >
              <option value="">Selecione</option>
              {organizationsForSubscriptions.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Plano
            <select
              required
              value={subscriptionForm.planId}
              onChange={(event) =>
                setSubscriptionForm({
                  ...subscriptionForm,
                  planId: event.target.value
                })
              }
            >
              <option value="">Selecione</option>
              {plans.map((plan) => {
                const productRelation = plan.products;
                const product = Array.isArray(productRelation)
                  ? productRelation[0]
                  : productRelation;

                return (
                  <option key={plan.id} value={plan.id}>
                    {product?.name ?? "Sistema"} · {plan.name}
                  </option>
                );
              })}
            </select>
          </label>

          <label>
            Status
            <select
              value={subscriptionForm.status}
              onChange={(event) =>
                setSubscriptionForm({
                  ...subscriptionForm,
                  status: event.target.value
                })
              }
            >
              <option value="trialing">Em teste</option>
              <option value="active">Ativa</option>
              <option value="past_due">Pagamento pendente</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </label>

          <label>
            Fim do período
            <input
              type="date"
              value={subscriptionForm.currentPeriodEnd}
              onChange={(event) =>
                setSubscriptionForm({
                  ...subscriptionForm,
                  currentPeriodEnd: event.target.value
                })
              }
            />
          </label>

          <button className="button button-primary" type="submit">
            Salvar assinatura
          </button>
        </form>
      </section>

      <div className="plan-grid admin-plan-grid">
        {plans.map((plan) => {
          const productRelation = plan.products;
          const product = Array.isArray(productRelation)
            ? productRelation[0]
            : productRelation;

          return (
            <article className="plan-card" key={plan.id}>
              <span className="status-pill">{product?.name ?? "Produto"}</span>
              <h3>{plan.name}</h3>
              <strong className="plan-price">{formatPlanPrice(plan.price_cents, plan.currency)}</strong>
              <p>{plan.description ?? "Sem descrição."}</p>
              <small>
                {translateBillingCycle(plan.billing_cycle)}
                {plan.trial_days > 0 ? ` · teste de ${plan.trial_days} dias` : ""}
              </small>
            </article>
          );
        })}
      </div>

      <section className="panel subscriptions-admin-panel">
        <small>ASSINATURAS</small>
        <h2>Assinaturas registradas</h2>
        <div className="subscription-table">
          {subscriptions.length > 0 ? (
            subscriptions.map((subscription) => {
              const orgRelation = subscription.organizations;
              const organization = Array.isArray(orgRelation)
                ? orgRelation[0]
                : orgRelation;
              const productRelation = subscription.products;
              const product = Array.isArray(productRelation)
                ? productRelation[0]
                : productRelation;
              const planRelation = subscription.plans;
              const plan = Array.isArray(planRelation) ? planRelation[0] : planRelation;

              return (
                <div className="subscription-row" key={subscription.id}>
                  <strong>{organization?.name ?? "Organização"}</strong>
                  <span>{product?.name ?? "Sistema"}</span>
                  <span>{plan?.name ?? "Plano"}</span>
                  <select
                    value={subscription.status}
                    onChange={(event) =>
                      void updateSubscriptionStatus(
                        subscription.id,
                        event.target.value
                      )
                    }
                  >
                    <option value="trialing">Em teste</option>
                    <option value="active">Ativa</option>
                    <option value="past_due">Pagamento pendente</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>
              );
            })
          ) : (
            <p>Nenhuma assinatura cadastrada.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function AdminPaymentsPage() {
  const workspace = useWorkspace();
  const [payments, setPayments] = useState<SaaSPayment[]>([]);
  const [subscriptions, setSubscriptions] = useState<SaaSSubscription[]>([]);
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [gatewayEnvironment, setGatewayEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [gatewayStatus, setGatewayStatus] = useState<"checking" | "ok" | "error">("checking");
  const [gatewayStatusMessage, setGatewayStatusMessage] = useState("Verificando conexão com o Asaas...");
  const [generatingPaymentId, setGeneratingPaymentId] = useState<string | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    amount: "",
    dueDate: "",
    paymentMethod: ""
  });
  const [form, setForm] = useState({
    organizationId: "",
    subscriptionId: "",
    amount: "0",
    dueDate: "",
    status: "pending",
    paymentMethod: ""
  });

  async function loadPaymentData() {
    if (!supabase || !workspace.isPlatformAdmin) return;

    setLoading(true);

    const [paymentResult, subscriptionResult, organizationResult] =
      await Promise.all([
        supabase
          .from("payments")
          .select(
            "id, organization_id, subscription_id, amount_cents, currency, status, due_date, paid_at, provider, provider_payment_id, provider_status, invoice_url, payment_method, organizations(name)"
          )
          .order("created_at", { ascending: false }),
        supabase
          .from("subscriptions")
          .select(
            "id, organization_id, product_id, plan_id, status, starts_at, trial_ends_at, current_period_end, organizations(name), products(name), plans(name, plan_key)"
          ),
        supabase.from("organizations").select("id, name, slug").order("name")
      ]);

    const firstError =
      paymentResult.error ||
      subscriptionResult.error ||
      organizationResult.error;

    if (firstError) {
      setMessage(firstError.message);
      setLoading(false);
      return;
    }

    setPayments((paymentResult.data ?? []) as SaaSPayment[]);
    setSubscriptions((subscriptionResult.data ?? []) as SaaSSubscription[]);
    setOrganizations((organizationResult.data ?? []) as AdminOrganization[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadPaymentData();
  }, [workspace.isPlatformAdmin]);

  useEffect(() => {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const client = supabase;

    async function loadGatewayStatus() {
      setGatewayStatus("checking");
      setGatewayStatusMessage("Verificando conexão com o Asaas...");

      const {
        data: { session }
      } = await client.auth.getSession();

      if (!session?.access_token) {
        setGatewayStatus("error");
        setGatewayStatusMessage("Sessão expirada. Entre novamente.");
        return;
      }

      try {
        const response = await fetch("/api/asaas/status", {
          headers: {
            Authorization: "Bearer " + session.access_token
          }
        });

        const text = await response.text();
        let result:
          | {
              ok?: boolean;
              environment?: "sandbox" | "production";
              message?: string;
              error?: string;
            }
          | null = null;

        if (text) {
          try {
            result = JSON.parse(text) as {
              ok?: boolean;
              environment?: "sandbox" | "production";
              message?: string;
              error?: string;
            };
          } catch {
            result = null;
          }
        }

        if (result?.environment) {
          setGatewayEnvironment(result.environment);
        }

        if (!response.ok || !result?.ok) {
          setGatewayStatus("error");
          setGatewayStatusMessage(
            result?.error || "Não foi possível validar a conexão com o Asaas."
          );
          return;
        }

        setGatewayStatus("ok");
        setGatewayStatusMessage(
          result.message || "Conexão com o Asaas validada."
        );
      } catch (error) {
        setGatewayStatus("error");
        setGatewayStatusMessage(
          error instanceof Error ? error.message : "Falha inesperada."
        );
      }
    }

    void loadGatewayStatus();
  }, [workspace.isPlatformAdmin]);

  async function createPayment(event: FormEvent) {
    event.preventDefault();

    if (!supabase || !workspace.isPlatformAdmin) return;

    const normalizedAmount = Number(
      form.amount.replace(".", "").replace(",", ".")
    );

    if (!form.organizationId || !form.dueDate || Number.isNaN(normalizedAmount)) {
      setMessage("Preencha organização, valor e vencimento da cobrança.");
      return;
    }

    if (gatewayEnvironment === "sandbox" && normalizedAmount < 5) {
      setMessage("Para o teste no Asaas Sandbox, use uma cobrança de pelo menos R$ 5,00.");
      return;
    }

    const { error } = await supabase.from("payments").insert({
      organization_id: form.organizationId,
      subscription_id: form.subscriptionId || null,
      amount_cents: Math.round(normalizedAmount * 100),
      currency: "BRL",
      status: form.status,
      due_date: form.dueDate || null,
      payment_method: form.paymentMethod || null,
      provider: "manual"
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Cobrança criada com sucesso.");
    setForm({
      organizationId: "",
      subscriptionId: "",
      amount: "0",
      dueDate: "",
      status: "pending",
      paymentMethod: ""
    });
    await loadPaymentData();
  }

  async function updatePaymentStatus(paymentId: string, status: string) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const payload: { status: string; paid_at?: string | null } = { status };

    if (status === "paid") payload.paid_at = new Date().toISOString();
    if (status !== "paid") payload.paid_at = null;

    const { error } = await supabase
      .from("payments")
      .update(payload)
      .eq("id", paymentId);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Status da cobrança atualizado.");
    await loadPaymentData();
  }

  function beginEditLocalPayment(payment: SaaSPayment) {
    if (payment.provider_payment_id) {
      setMessage(
        "Cobranças já enviadas ao Asaas não podem ser alteradas apenas localmente."
      );
      return;
    }

    setEditingPaymentId(payment.id);
    setEditForm({
      amount: (payment.amount_cents / 100).toFixed(2).replace(".", ","),
      dueDate: payment.due_date ?? "",
      paymentMethod: payment.payment_method ?? ""
    });
    setMessage("");
  }

  function cancelEditLocalPayment() {
    setEditingPaymentId(null);
    setEditForm({
      amount: "",
      dueDate: "",
      paymentMethod: ""
    });
  }

  async function saveEditLocalPayment(payment: SaaSPayment) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    const normalizedAmount = Number(
      editForm.amount.replace(".", "").replace(",", ".")
    );

    if (!editForm.dueDate || Number.isNaN(normalizedAmount)) {
      setMessage("Preencha valor e vencimento da cobrança.");
      return;
    }

    if (gatewayEnvironment === "sandbox" && normalizedAmount < 5) {
      setMessage("Para o teste no Asaas Sandbox, use uma cobrança de pelo menos R$ 5,00.");
      return;
    }

    const { error } = await supabase
      .from("payments")
      .update({
        amount_cents: Math.round(normalizedAmount * 100),
        due_date: editForm.dueDate,
        payment_method: editForm.paymentMethod || null
      })
      .eq("id", payment.id)
      .is("provider_payment_id", null);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Cobrança local atualizada com sucesso.");
    cancelEditLocalPayment();
    await loadPaymentData();
  }

  async function deleteLocalPayment(payment: SaaSPayment) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    if (payment.provider_payment_id) {
      setMessage(
        "Cobranças já enviadas ao Asaas não podem ser excluídas do histórico."
      );
      return;
    }

    const confirmed = window.confirm(
      "Excluir esta cobrança local? Esta ação não poderá ser desfeita."
    );

    if (!confirmed) return;

    const { error } = await supabase
      .from("payments")
      .delete()
      .eq("id", payment.id)
      .is("provider_payment_id", null);

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage("Cobrança local excluída com sucesso.");
    await loadPaymentData();
  }

  async function createAsaasPayment(payment: SaaSPayment) {
    if (!supabase || !workspace.isPlatformAdmin) return;

    if (!payment.due_date) {
      setMessage("Defina um vencimento antes de gerar a cobrança no Asaas.");
      return;
    }

    setGeneratingPaymentId(payment.id);
    setMessage(
      gatewayEnvironment === "production"
        ? "Enviando cobrança ao Asaas Produção..."
        : "Enviando cobrança ao Asaas Sandbox..."
    );

    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setMessage("Sua sessão expirou. Entre novamente.");
        return;
      }

      const normalizedMethod = (payment.payment_method ?? "").toUpperCase();
      const billingType = ["PIX", "BOLETO", "CREDIT_CARD"].includes(normalizedMethod)
        ? normalizedMethod
        : "UNDEFINED";

      const response = await fetch("/api/asaas/create-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + session.access_token
        },
        body: JSON.stringify({
          paymentId: payment.id,
          billingType
        })
      });

      const responseText = await response.text();
      let result:
        | {
            error?: string;
            invoiceUrl?: string;
            environment?: "sandbox" | "production";
          }
        | null = null;

      if (responseText) {
        try {
          result = JSON.parse(responseText) as {
            error?: string;
            invoiceUrl?: string;
            environment?: "sandbox" | "production";
          };
        } catch {
          result = null;
        }
      }

      if (!response.ok) {
        const detail =
          result?.error ||
          (responseText && responseText.length < 240 ? responseText : "") ||
          `A função do Asaas respondeu HTTP ${response.status}.`;

        setMessage("Erro ao gerar no Asaas: " + detail);
        return;
      }

      const responseEnvironment =
        result?.environment ?? gatewayEnvironment;
      const environmentLabel =
        responseEnvironment === "production" ? "Produção" : "Sandbox";

      setMessage(
        result?.invoiceUrl
          ? "Cobrança criada no Asaas " +
              environmentLabel +
              ". Use “Abrir Asaas” para visualizar."
          : "Cobrança criada no Asaas " +
              environmentLabel +
              " com sucesso."
      );

      await loadPaymentData();
    } catch (error) {
      setMessage(
        "Erro de conexão com o Asaas: " +
          (error instanceof Error ? error.message : "falha inesperada.")
      );
    } finally {
      setGeneratingPaymentId(null);
    }
  }

  if (!workspace.isPlatformAdmin) {
    return (
      <section className="panel empty-state">
        <strong>Acesso restrito.</strong>
        <p>Esta área é exclusiva da administração da plataforma.</p>
      </section>
    );
  }

  if (loading) {
    return <section className="panel">Carregando cobranças...</section>;
  }

  return (
    <div>
      <div className="page-title">
        <span className="eyebrow">
          GATEWAY ASAAS · {gatewayEnvironment === "production" ? "PRODUÇÃO" : "SANDBOX"} · CICLO 17
        </span>
        <h1>Cobranças da plataforma</h1>
        <p>
          O mesmo fluxo agora suporta Sandbox e Produção com validação segura do ambiente.
        </p>
      </div>

      <section className={"panel gateway-status-card gateway-" + gatewayStatus}>
        <div>
          <small>STATUS DO GATEWAY</small>
          <h2>
            {gatewayEnvironment === "production"
              ? "Asaas Produção"
              : "Asaas Sandbox"}
          </h2>
          <p>{gatewayStatusMessage}</p>
        </div>
        <span className="status-pill">
          {gatewayStatus === "checking"
            ? "Verificando"
            : gatewayStatus === "ok"
              ? "Conectado"
              : "Atenção"}
        </span>
      </section>

      {message && <p className="form-message">{message}</p>}

      <section className="panel admin-plan-form">
        <small>NOVA COBRANÇA</small>
        <h2>Criar cobrança</h2>

        <form className="plan-form-grid" onSubmit={createPayment}>
          <label>
            Organização
            <select
              required
              value={form.organizationId}
              onChange={(event) =>
                setForm({
                  ...form,
                  organizationId: event.target.value,
                  subscriptionId: ""
                })
              }
            >
              <option value="">Selecione</option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Assinatura
            <select
              value={form.subscriptionId}
              onChange={(event) =>
                setForm({ ...form, subscriptionId: event.target.value })
              }
            >
              <option value="">Sem vínculo</option>
              {subscriptions
                .filter(
                  (subscription) =>
                    !form.organizationId ||
                    subscription.organization_id === form.organizationId
                )
                .map((subscription) => {
                  const productRelation = subscription.products;
                  const product = Array.isArray(productRelation)
                    ? productRelation[0]
                    : productRelation;
                  const planRelation = subscription.plans;
                  const plan = Array.isArray(planRelation)
                    ? planRelation[0]
                    : planRelation;

                  return (
                    <option key={subscription.id} value={subscription.id}>
                      {product?.name ?? "Sistema"} · {plan?.name ?? "Plano"}
                    </option>
                  );
                })}
            </select>
          </label>

          <label>
            Valor em R$
            <input
              required
              value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
              inputMode="decimal"
              placeholder="49,90"
            />
            {gatewayEnvironment === "sandbox" ? (
              <small>No Sandbox, use R$ 5,00 ou mais.</small>
            ) : (
              <small>Ambiente de Produção: cobranças reais.</small>
            )}
          </label>

          <label>
            Vencimento
            <input
              required
              type="date"
              value={form.dueDate}
              onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
            />
          </label>

          <label>
            Status
            <select
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value })}
            >
              <option value="pending">Pendente</option>
              <option value="paid">Pago</option>
              <option value="failed">Falhou</option>
              <option value="refunded">Reembolsado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </label>

          <label>
            Método
            <select
              value={form.paymentMethod}
              onChange={(event) =>
                setForm({ ...form, paymentMethod: event.target.value })
              }
            >
              <option value="">Selecione</option>
              <option value="PIX">Pix</option>
              <option value="BOLETO">Boleto</option>
              <option value="CREDIT_CARD">Cartão</option>
            </select>
          </label>

          <button className="button button-primary" type="submit">
            Criar cobrança
          </button>
        </form>
      </section>

      <section className="panel">
        <small>HISTÓRICO</small>
        <h2>Cobranças registradas</h2>

        <div className="payment-table">
          {payments.length > 0 ? (
            payments.map((payment) => {
              const orgRelation = payment.organizations;
              const organization = Array.isArray(orgRelation)
                ? orgRelation[0]
                : orgRelation;
              const isEditing = editingPaymentId === payment.id;

              return (
                <div className="payment-row admin-payment-row" key={payment.id}>
                  <strong>{organization?.name ?? "Organização"}</strong>

                  {isEditing ? (
                    <input
                      className="payment-inline-input"
                      value={editForm.amount}
                      onChange={(event) =>
                        setEditForm({ ...editForm, amount: event.target.value })
                      }
                      inputMode="decimal"
                      aria-label="Valor da cobrança"
                    />
                  ) : (
                    <span>{formatPlanPrice(payment.amount_cents, payment.currency)}</span>
                  )}

                  {isEditing ? (
                    <input
                      className="payment-inline-input"
                      type="date"
                      value={editForm.dueDate}
                      onChange={(event) =>
                        setEditForm({ ...editForm, dueDate: event.target.value })
                      }
                      aria-label="Vencimento da cobrança"
                    />
                  ) : (
                    <span>
                      {payment.due_date
                        ? new Date(payment.due_date + "T12:00:00").toLocaleDateString("pt-BR")
                        : "Sem vencimento"}
                    </span>
                  )}

                  <select
                    value={payment.status}
                    disabled={isEditing}
                    onChange={(event) =>
                      void updatePaymentStatus(payment.id, event.target.value)
                    }
                  >
                    <option value="pending">Pendente</option>
                    <option value="paid">Pago</option>
                    <option value="failed">Falhou</option>
                    <option value="refunded">Reembolsado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>

                  <div className="payment-action">
                    {payment.provider_payment_id ? (
                      payment.invoice_url ? (
                        <a
                          className="button button-secondary"
                          href={payment.invoice_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Abrir Asaas
                        </a>
                      ) : (
                        <span>{payment.provider_status ?? "Enviado ao Asaas"}</span>
                      )
                    ) : isEditing ? (
                      <>
                        <select
                          className="payment-inline-select"
                          value={editForm.paymentMethod}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              paymentMethod: event.target.value
                            })
                          }
                          aria-label="Método de pagamento"
                        >
                          <option value="">Método</option>
                          <option value="PIX">Pix</option>
                          <option value="BOLETO">Boleto</option>
                          <option value="CREDIT_CARD">Cartão</option>
                        </select>
                        <button
                          className="button button-primary"
                          type="button"
                          onClick={() => void saveEditLocalPayment(payment)}
                        >
                          Salvar
                        </button>
                        <button
                          className="button button-secondary"
                          type="button"
                          onClick={cancelEditLocalPayment}
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="button button-secondary"
                          type="button"
                          onClick={() => beginEditLocalPayment(payment)}
                        >
                          Editar
                        </button>
                        <button
                          className="button button-secondary"
                          type="button"
                          disabled={generatingPaymentId === payment.id}
                          onClick={() => void createAsaasPayment(payment)}
                        >
                          {generatingPaymentId === payment.id
                            ? "Enviando..."
                            : "Gerar no Asaas"}
                        </button>
                        <button
                          className="button button-danger"
                          type="button"
                          disabled={generatingPaymentId === payment.id}
                          onClick={() => void deleteLocalPayment(payment)}
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p>Nenhuma cobrança cadastrada.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function ProtectedArea({
  auth,
  children
}: {
  auth: AuthState;
  children: ReactNode;
}) {
  if (auth.loading) {
    return <div className="center-screen">Carregando plataforma...</div>;
  }

  if (!supabaseConfigured) {
    return (
      <div className="setup-screen">
        <Brand />
        <div className="setup-card">
          <span className="eyebrow">FUNDAÇÃO SAAS CRIADA</span>
          <h1>Agora precisamos conectar o Supabase.</h1>
          <p>
            A área autenticada está implementada, mas só será liberada com as variáveis
            VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY.
          </p>
          <Link className="button button-primary" to="/">
            Voltar para a Home
          </Link>
        </div>
      </div>
    );
  }

  if (!auth.user) return <Navigate to="/login" replace />;

  return <AppShell user={auth.user}>{children}</AppShell>;
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({
    session: null,
    user: null,
    loading: supabaseConfigured
  });

  useEffect(() => {
    if (!supabase) {
      setAuth({ session: null, user: null, loading: false });
      return;
    }

    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setAuth({
        session: data.session,
        user: data.session?.user ?? null,
        loading: false
      });
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuth({
        session,
        user: session?.user ?? null,
        loading: false
      });
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const protectedAuth = useMemo(() => auth, [auth]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={auth.user ? <Navigate to="/app" replace /> : <LoginPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
      <Route path="/auth/confirm" element={<ConfirmEmailPage />} />
      <Route path="/convite/aceitar" element={<InviteAcceptPage />} />
      <Route
        path="/app"
        element={
          <ProtectedArea auth={protectedAuth}>
            <DashboardPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/sistemas"
        element={
          <ProtectedArea auth={protectedAuth}>
            <SystemsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/planos"
        element={
          <ProtectedArea auth={protectedAuth}>
            <PlansPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/pagamentos"
        element={
          <ProtectedArea auth={protectedAuth}>
            <PaymentsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/equipe"
        element={
          <ProtectedArea auth={protectedAuth}>
            <TeamPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/perfil"
        element={
          <ProtectedArea auth={protectedAuth}>
            <ProfilePage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/notificacoes"
        element={
          <ProtectedArea auth={protectedAuth}>
            <NotificationsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/atividades"
        element={
          <ProtectedArea auth={protectedAuth}>
            <ActivityHistoryPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/configuracoes"
        element={
          <ProtectedArea auth={protectedAuth}>
            <SettingsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/app/suporte"
        element={
          <ProtectedArea auth={protectedAuth}>
            <SupportPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminDashboardPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/organizacoes/:organizationId"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminOrganizationDetailPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/sistemas"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminProductsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/planos"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminPlansPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/pagamentos"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminPaymentsPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/suporte"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminSupportPage />
          </ProtectedArea>
        }
      />
      <Route
        path="/admin/lancamento"
        element={
          <ProtectedArea auth={protectedAuth}>
            <AdminLaunchPage />
          </ProtectedArea>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
